import type { AdminDashboardRuntimeConfig } from "../../app/composition/admin-dashboard-runtime-config";
import type {
  AdminAppSignalKey,
  AdminAppSignalStage,
  AdminFailedFlow,
  AdminIncidentLogArtifactData,
  AdminFailedFlowPage,
  AdminMediaArtifactData,
  AdminProjectionFreshness,
  AdminQueryMeta,
} from "../../contracts/admin-read-models";
import type { AdminDashboardLocale } from "../../shared/locale/admin-dashboard-locale";
import {
  buildAdminObservabilityAggregateViewModel,
  type AdminObservabilityAggregateViewModel,
} from "../../shared/locale/admin-observability-aggregate-summary";
import {
  buildAdminFailedFlowRowHotspotViewModel,
  type AdminFailedFlowRowHotspotViewModel,
} from "../../shared/locale/admin-failed-flow-row-hotspot-summary";
import { pickAdminDashboardLocaleText } from "../../shared/locale/admin-dashboard-copy";
import {
  formatAdminAccountIdentityLabel,
  formatAdminLinkedProviderCountLabel,
  formatAdminProviderIdentityLabel,
} from "../../shared/formatting/format-admin-account-identity";
import { formatAdminAuthProviderKindLabel } from "../../shared/formatting/format-admin-auth-provider";
import { formatAdminCount } from "../../shared/formatting/format-admin-count";
import { formatAdminDate } from "../../shared/formatting/format-admin-date";
import {
  formatAdminChatMembershipCountBucketLabel,
  formatAdminChatMessageLengthBucketLabel,
  formatAdminChatRoomKindLabel,
  formatAdminCommunityContentLengthBucketLabel,
  formatAdminCommunityImageCountBucketLabel,
  formatAdminCommunityTopicTagLabel,
  formatAdminCommunityUploadSourceBucketLabel,
  formatAdminHealthAnalysisFallbackPathLabel,
  formatAdminHealthConfirmToSaveLatencyBucketLabel,
  formatAdminHealthFileSizeBucketLabel,
  formatAdminHealthFileTypeBucketLabel,
  formatAdminMedicationDosePresenceLabel,
  formatAdminMedicationOccurrenceActionKindLabel,
  formatAdminMedicationScheduleRecurrenceBucketLabel,
  formatAdminMedicationSnoozeWindowBucketLabel,
  formatAdminPetAttemptCountBucketLabel,
  formatAdminPetBreedSelectionBucketLabel,
  formatAdminPetPhotoConfirmLatencyBucketLabel,
  formatAdminPetProfileCompletionStepLabel,
  formatAdminPetSpeciesBucketLabel,
  formatAdminReceiptCategoryMutationTypeLabel,
  formatAdminReceiptExtractSourceBucketLabel,
  formatAdminReceiptImageByteSizeBucketLabel,
  formatAdminReceiptOcrConfidenceBucketLabel,
  formatAdminRideLongestAddressLengthBucketLabel,
  formatAdminRideVisitLeadTimeBucketLabel,
  formatAdminSocialProfileMutationFieldSetBucketLabel,
  formatAdminSocialPublicPetCountBucketLabel,
  formatAdminSocialTargetUserLookupKindLabel,
  formatAdminTelemetryAppBuildNumberLabel,
  formatAdminTelemetryDraftSourceBucketLabel,
  formatAdminTelemetryDeviceModelBucketLabel,
  formatAdminTelemetryDeviceModelNameBucketLabel,
  formatAdminTelemetryLocaleLabel,
  formatAdminTelemetryMediaAttachCountBucketLabel,
  formatAdminTelemetryNetworkTypeBucketLabel,
  formatAdminTelemetryOsMajorBucketLabel,
  formatAdminTelemetryOsVersionMinorBucketLabel,
  formatAdminTelemetryPhysicalDeviceBucketLabel,
  formatAdminTelemetryReauthMethodBucketLabel,
  formatAdminTelemetryRecentAuthAgeBucketLabel,
  formatAdminTelemetryRuntimeSummaryLabel,
  formatAdminTelemetryTimezoneNameBucketLabel,
  formatAdminTelemetryTimezoneOffsetBucketLabel,
  formatAdminBooleanLabel,
} from "../../shared/formatting/format-admin-runtime-context";
import {
  buildCloudLoggingRequestLogsUrl,
  buildCloudLoggingSimilarFailuresUrl,
} from "../../shared/telemetry/build-cloud-logging-url";
import { appendUserIncidentWorkspaceFocus } from "../users/incident-workspace-focus";
import {
  buildFailedFlowDrilldownTarget,
  type AdminFailedFlowDrilldownTarget,
  inferFailedFlowMediaLinkedEntity,
  inferFailedFlowMediaScenarioKey,
  isFailedFlowMediaRelevant,
} from "./failed-flows-use-cases";

export type FailedFlowRowViewModel = {
  id: string;
  createdAt: string;
  createdAtLabel: string;
  feature: string;
  useCase: string;
  signalKey: AdminAppSignalKey;
  stage: AdminAppSignalStage;
  status: "failed" | "partial";
  statusLabel: string;
  errorCode: string;
  statusCodeLabel: string;
  requestId?: string;
  correlationId?: string;
  jobId?: string;
  release?: string;
  appVersion?: string;
  runtimeAppBuildNumberLabel: string;
  platformLabel: string;
  runtimeLocaleLabel: string;
  runtimeTimezoneOffsetBucketLabel: string;
  runtimeTimezoneNameBucketLabel: string;
  runtimeDeviceModelBucketLabel: string;
  runtimeDeviceModelNameBucketLabel: string;
  runtimeOsMajorBucketLabel: string;
  runtimeOsVersionMinorBucketLabel: string;
  runtimeNetworkTypeBucketLabel: string;
  runtimePhysicalDeviceBucketLabel: string;
  providerKindLabel: string;
  reauthMethodBucketLabel: string;
  recentAuthAgeBucketLabel: string;
  requestSaveBeforeExitLabel: string;
  requestSuggestionUsedLabel: string;
  requestProfileCompletionStepLabel: string;
  requestAttemptCountBucketLabel: string;
  requestPhotoConfirmLatencyBucketLabel: string;
  requestDraftSourceBucketLabel: string;
  requestMediaAttachCountBucketLabel: string;
  requestSpeciesBucketLabel: string;
  requestBreedSelectionBucketLabel: string;
  requestTargetUserLookupKindLabel: string;
  requestPublicPetCountBucketLabel: string;
  requestProfileMutationFieldSetBucketLabel: string;
  requestRoomKindLabel: string;
  requestMessageLengthBucketLabel: string;
  requestMembershipCountBucketLabel: string;
  requestScheduleRecurrenceBucketLabel: string;
  requestDosePresenceLabel: string;
  requestOccurrenceActionKindLabel: string;
  requestSnoozeWindowBucketLabel: string;
  requestRideVisitLeadTimeBucketLabel: string;
  requestRideLongestAddressLengthBucketLabel: string;
  requestRideMemoProvidedLabel: string;
  requestCommunityUploadSourceBucketLabel: string;
  requestCommunityContentLengthBucketLabel: string;
  requestCommunityCommentLengthBucketLabel: string;
  requestCommunityImageCountBucketLabel: string;
  requestCommunityPetContextAttachedLabel: string;
  requestCommunityTopicTagLabel: string;
  requestCommunityRetryCountBucketLabel: string;
  requestReceiptExtractSourceBucketLabel: string;
  requestReceiptImageByteSizeBucketLabel: string;
  requestReceiptOcrConfidenceBucketLabel: string;
  requestReceiptManualOverrideLabel: string;
  requestReceiptCategoryMutationTypeLabel: string;
  requestHealthFileSizeBucketLabel: string;
  requestHealthFileTypeBucketLabel: string;
  requestHealthAnalysisFallbackPathLabel: string;
  requestHealthConfirmToSaveLatencyBucketLabel: string;
  deviceInstallId?: string;
  deviceInstallIdLabel: string;
  runtimeSummaryLabel: string;
  lastEventNameLabel: string;
  lastEventAtLabel: string;
  userIdHash?: string;
  accountIdentityLabel: string;
  accountProviderIdentityLabel: string;
  accountLinkedProviderCountLabel: string;
  petIdHash?: string;
  recordId?: string;
  receiptId?: string;
  fileId?: string;
  deletionJobId?: string;
  drilldownTarget: AdminFailedFlowDrilldownTarget;
  detail: FailedFlowDetailViewModel;
};

export type FailedFlowsViewModel = {
  totalCountLabel: string;
  generatedAtLabel: string;
  requestId: string;
  nextCursor?: string;
  selectedScopeLabel: string;
  distinctDeviceCountLabel: string;
  distinctUserCountLabel: string;
  topErrorCodeLabel: string;
  topErrorCountLabel: string;
  observabilityAggregate?: AdminObservabilityAggregateViewModel;
  rowHotspotSnapshot?: AdminFailedFlowRowHotspotViewModel;
  rows: FailedFlowRowViewModel[];
  sourceFreshness: Array<{
    source: string;
    freshness: AdminProjectionFreshness;
  }>;
};

export type FailedFlowIncidentRouteActionViewModel = {
  id: "requestLogs" | "userLookup" | "releaseRegression" | "jobsFallback" | "mediaLookup";
  label: string;
  summary: string;
  href?: string;
  linkLabel?: string;
};

export type FailedFlowTraceKeyViewModel = {
  id:
    | "requestId"
    | "correlationId"
    | "userIdHash"
    | "jobId"
    | "petIdHash"
    | "recordId"
    | "receiptId"
    | "fileId";
  label: string;
  value: string;
  href?: string;
};

export type FailedFlowRequestEvidenceViewModel = {
  summary: string;
  logAvailabilitySummary: string;
  traceKeys: FailedFlowTraceKeyViewModel[];
};

export type FailedFlowDetailViewModel = {
  id: string;
  createdAtLabel: string;
  signalKey: AdminAppSignalKey;
  stage: AdminAppSignalStage;
  status: "failed" | "partial";
  statusLabel: string;
  errorCode: string;
  statusCodeLabel: string;
  requestId?: string;
  correlationId?: string;
  jobId?: string;
  userIdHash?: string;
  accountIdentityLabel: string;
  accountProviderIdentityLabel: string;
  accountLinkedProviderCountLabel: string;
  petIdHash?: string;
  recordId?: string;
  receiptId?: string;
  fileId?: string;
  deletionJobId?: string;
  release?: string;
  appVersion?: string;
  runtimeAppBuildNumberLabel: string;
  platformLabel: string;
  runtimeLocaleLabel: string;
  runtimeTimezoneOffsetBucketLabel: string;
  runtimeTimezoneNameBucketLabel: string;
  runtimeDeviceModelBucketLabel: string;
  runtimeDeviceModelNameBucketLabel: string;
  runtimeOsMajorBucketLabel: string;
  runtimeOsVersionMinorBucketLabel: string;
  runtimeNetworkTypeBucketLabel: string;
  runtimePhysicalDeviceBucketLabel: string;
  runtimeSummaryLabel: string;
  providerKindLabel: string;
  reauthMethodBucketLabel: string;
  recentAuthAgeBucketLabel: string;
  requestSaveBeforeExitLabel: string;
  requestSuggestionUsedLabel: string;
  requestProfileCompletionStepLabel: string;
  requestAttemptCountBucketLabel: string;
  requestPhotoConfirmLatencyBucketLabel: string;
  requestDraftSourceBucketLabel: string;
  requestMediaAttachCountBucketLabel: string;
  requestSpeciesBucketLabel: string;
  requestBreedSelectionBucketLabel: string;
  requestTargetUserLookupKindLabel: string;
  requestPublicPetCountBucketLabel: string;
  requestProfileMutationFieldSetBucketLabel: string;
  requestRoomKindLabel: string;
  requestMessageLengthBucketLabel: string;
  requestMembershipCountBucketLabel: string;
  requestScheduleRecurrenceBucketLabel: string;
  requestDosePresenceLabel: string;
  requestOccurrenceActionKindLabel: string;
  requestSnoozeWindowBucketLabel: string;
  requestRideVisitLeadTimeBucketLabel: string;
  requestRideLongestAddressLengthBucketLabel: string;
  requestRideMemoProvidedLabel: string;
  requestCommunityUploadSourceBucketLabel: string;
  requestCommunityContentLengthBucketLabel: string;
  requestCommunityCommentLengthBucketLabel: string;
  requestCommunityImageCountBucketLabel: string;
  requestCommunityPetContextAttachedLabel: string;
  requestCommunityTopicTagLabel: string;
  requestCommunityRetryCountBucketLabel: string;
  requestReceiptExtractSourceBucketLabel: string;
  requestReceiptImageByteSizeBucketLabel: string;
  requestReceiptOcrConfidenceBucketLabel: string;
  requestReceiptManualOverrideLabel: string;
  requestReceiptCategoryMutationTypeLabel: string;
  requestHealthFileSizeBucketLabel: string;
  requestHealthFileTypeBucketLabel: string;
  requestHealthAnalysisFallbackPathLabel: string;
  requestHealthConfirmToSaveLatencyBucketLabel: string;
  deviceInstallId?: string;
  deviceInstallIdLabel: string;
  lastEventNameLabel: string;
  lastEventAtLabel: string;
  drilldownTarget: AdminFailedFlowDrilldownTarget;
  observability: {
    incidentSummary: string;
    firstCheckLabel: string;
    requestLogsUrl?: string;
    similarFailuresUrl?: string;
  };
  requestEvidence: FailedFlowRequestEvidenceViewModel;
  mediaContext?: {
    headline: string;
    sourceScenarioLabel: string;
    artifactIdentityLabel: string;
    linkedEntityLabel: string;
    mediaKindLabel: string;
    summary: string;
    inventoryStateLabel?: string;
    missingReasonLabel?: string;
    nextActionLabel?: string;
    relationTypeLabel?: string;
    artifactRoleLabel?: string;
    previewAvailabilityLabel?: string;
    downloadAvailabilityLabel?: string;
    fallbackReasonLabel?: string;
    binaryReviewPathLabel?: string;
    workspaceHref?: string;
    workspaceLinkLabel?: string;
  };
  sameDeviceLogContext?: {
    headline: string;
    artifactIdentityLabel: string;
    captureTriggerLabel: string;
    previewReadinessLabel: string;
    downloadReadinessLabel: string;
    bundleHashPresenceLabel: string;
    manifestVersionLabel: string;
    deliveryPathStateLabel: string;
    previewHeadlineLabel: string;
    latestRequestIdLabel: string;
    latestCorrelationIdLabel: string;
    summary: string;
    workspaceHref?: string;
    workspaceLinkLabel?: string;
    requestTraceHref?: string;
    requestTraceLinkLabel?: string;
  };
  incidentRoute: {
    headline: string;
    firstCheckLabel: string;
    actions: FailedFlowIncidentRouteActionViewModel[];
  };
  jobLabel?: string;
};

function text(locale: AdminDashboardLocale, en: string, ko: string): string {
  return pickAdminDashboardLocaleText(locale, { en, ko });
}

function formatMediaRelationTypeLabel(
  relationType: AdminMediaArtifactData["relationType"],
  locale: AdminDashboardLocale,
): string {
  switch (relationType) {
    case "direct":
      return text(locale, "Direct", "직접 연결");
    case "contextual":
      return text(locale, "Contextual", "문맥 연결");
  }
}

function formatMediaArtifactRoleLabel(
  artifactRole: AdminMediaArtifactData["artifactRole"],
  locale: AdminDashboardLocale,
): string {
  switch (artifactRole) {
    case "representative":
      return text(locale, "Representative", "대표");
    case "gallery_member":
      return text(locale, "Gallery member", "갤러리 구성원");
    case "original_only":
      return text(locale, "Original only", "원본 전용");
    case "evidence_sidecar":
      return text(locale, "Evidence sidecar", "증거 보조");
  }
}

function formatMediaAvailabilityLabel(
  availability: AdminMediaArtifactData["previewAvailability"],
  locale: AdminDashboardLocale,
): string {
  switch (availability) {
    case "available":
      return text(locale, "Available", "가능");
    case "pending_policy_check":
      return text(locale, "Pending policy check", "정책 확인 대기");
    case "unsupported_type":
      return text(locale, "Unsupported type", "지원되지 않는 형식");
    case "not_generated":
      return text(locale, "Not generated", "생성되지 않음");
    case "deleted":
      return text(locale, "Deleted", "삭제됨");
    case "retention_expired":
      return text(locale, "Retention expired", "보관 기간 만료");
    case "access_denied":
      return text(locale, "Access denied", "접근 거부");
    case "unknown":
      return text(locale, "Unknown", "미확인");
  }
}

function formatMediaFallbackReasonLabel(
  fallbackReason: AdminMediaArtifactData["fallbackReason"],
  locale: AdminDashboardLocale,
): string {
  switch (fallbackReason) {
    case undefined:
      return "-";
    case "missing":
      return text(locale, "Missing", "누락");
    case "build_not_supported":
      return text(locale, "Build not supported", "빌드 미지원");
    case "capture_pending":
      return text(locale, "Capture pending", "수집 대기");
    case "upload_failed":
      return text(locale, "Upload failed", "업로드 실패");
    case "unsupported_type":
      return text(locale, "Unsupported type", "지원되지 않는 형식");
    case "pending_confirm":
      return text(locale, "Pending confirm", "확정 대기");
    case "cleanup_scheduled":
      return text(locale, "Cleanup scheduled", "정리 예정");
    case "deleted":
      return text(locale, "Deleted", "삭제됨");
    case "retention_expired":
      return text(locale, "Retention expired", "보관 기간 만료");
    case "access_denied":
      return text(locale, "Access denied", "접근 거부");
    case "manual_resend_required":
      return text(locale, "Manual resend required", "수동 재전송 필요");
    case "unknown":
      return text(locale, "Unknown", "미확인");
  }
}

function pickArtifactBinaryReviewPathLabel(
  artifact: Pick<AdminMediaArtifactData, "previewAvailability" | "downloadAvailability" | "fallbackReason">,
  locale: AdminDashboardLocale,
): string {
  if (artifact.previewAvailability === "available") {
    return text(locale, "Preview first", "미리보기 우선");
  }

  if (artifact.downloadAvailability === "available") {
    return text(locale, "Download first", "다운로드 우선");
  }

  if (artifact.fallbackReason === "missing") {
    return text(locale, "Source trace first", "source 추적 우선");
  }

  return text(locale, "Trace and fallback first", "추적 및 fallback 우선");
}

function formatIncidentLogCaptureTriggerLabel(
  captureTrigger: AdminIncidentLogArtifactData["captureTrigger"],
  locale: AdminDashboardLocale,
): string {
  switch (captureTrigger) {
    case "failed_request":
      return text(locale, "Failed request", "실패 요청");
    case "fatal_boundary":
      return text(locale, "Fatal boundary", "치명 경계");
    case "operator_resend":
      return text(locale, "Operator resend", "운영자 재전송");
    case "upload_recovery":
      return text(locale, "Upload recovery", "업로드 복구");
    case "unknown":
      return text(locale, "Unknown", "미확인");
  }
}

function formatIncidentLogPreviewReadinessLabel(
  previewReadiness: AdminIncidentLogArtifactData["previewReadiness"],
  locale: AdminDashboardLocale,
): string {
  switch (previewReadiness) {
    case "ready":
      return text(locale, "Ready", "준비");
    case "processing":
      return text(locale, "Processing", "처리 중");
    case "unsupported":
      return text(locale, "Unsupported", "미지원");
    case "empty_excerpt":
      return text(locale, "Empty excerpt", "발췌 없음");
    case "retention_expired":
      return text(locale, "Retention expired", "보관 기간 만료");
    case "access_denied":
      return text(locale, "Access denied", "접근 거부");
    case "unknown":
      return text(locale, "Unknown", "미확인");
  }
}

function formatIncidentLogDownloadReadinessLabel(
  downloadReadiness: AdminIncidentLogArtifactData["downloadReadiness"],
  locale: AdminDashboardLocale,
): string {
  switch (downloadReadiness) {
    case "ready":
      return text(locale, "Ready", "준비");
    case "processing":
      return text(locale, "Processing", "처리 중");
    case "missing_manifest":
      return text(locale, "Missing manifest", "manifest 누락");
    case "retention_expired":
      return text(locale, "Retention expired", "보관 기간 만료");
    case "access_denied":
      return text(locale, "Access denied", "접근 거부");
    case "unknown":
      return text(locale, "Unknown", "미확인");
  }
}

function formatIncidentLogBundleHashPresenceLabel(
  bundleHashPresence: AdminIncidentLogArtifactData["bundleHashPresence"],
  locale: AdminDashboardLocale,
): string {
  switch (bundleHashPresence) {
    case "present":
      return text(locale, "Present", "있음");
    case "missing":
      return text(locale, "Missing", "누락");
    case "unknown":
      return text(locale, "Unknown", "미확인");
  }
}

function pickIncidentLogDeliveryPathStateLabel(
  artifact: AdminIncidentLogArtifactData,
  locale: AdminDashboardLocale,
): string {
  if (
    artifact.supportabilityState === "supported" &&
    artifact.downloadReadiness === "ready" &&
    artifact.handoffReadiness === "ready" &&
    artifact.bundleHashPresence === "present" &&
    artifact.manifestVersion
  ) {
    return text(locale, "Producer-to-storage ready", "producer-to-storage 준비 완료");
  }

  if (
    artifact.supportabilityState === "capture_pending" ||
    artifact.supportabilityState === "upload_in_progress" ||
    artifact.downloadReadiness === "processing"
  ) {
    return text(locale, "Capture/upload pending", "수집/업로드 진행 중");
  }

  if (
    artifact.supportabilityState === "upload_failed" ||
    artifact.supportabilityState === "manual_resend_required" ||
    artifact.handoffReadiness === "needs_resend"
  ) {
    return text(locale, "Upload failed or resend required", "업로드 실패 또는 재전송 필요");
  }

  if (
    artifact.downloadReadiness === "missing_manifest" ||
    artifact.bundleHashPresence === "missing"
  ) {
    return text(locale, "Manifest recovery needed", "manifest 복구 필요");
  }

  if (
    artifact.supportabilityState === "retention_expired" ||
    artifact.downloadReadiness === "retention_expired" ||
    artifact.previewReadiness === "retention_expired"
  ) {
    return text(locale, "Retention expired", "보관 기간 만료");
  }

  if (
    artifact.supportabilityState === "access_denied" ||
    artifact.downloadReadiness === "access_denied" ||
    artifact.previewReadiness === "access_denied" ||
    artifact.handoffReadiness === "blocked_by_policy"
  ) {
    return text(locale, "Policy blocked", "정책 차단");
  }

  return text(locale, "Supportability review required", "지원 상태 추가 확인 필요");
}

function toRowStatus(
  status: AdminFailedFlow["status"],
  locale: AdminDashboardLocale,
): Pick<FailedFlowRowViewModel, "status" | "statusLabel"> {
  if (status === "failed") {
    return {
      status: "failed",
      statusLabel: text(locale, "Failed", "실패"),
    };
  }

  if (status === "stalled") {
    return {
      status: "partial",
      statusLabel: text(locale, "Stalled", "정지"),
    };
  }

  return {
    status: "partial",
    statusLabel: text(locale, "Partial", "부분"),
  };
}

function toStatusCodeLabel(value: number | undefined): string {
  return value === undefined ? "-" : String(value);
}

function toRequestEvidenceStatusSummary(
  flow: AdminFailedFlow,
  locale: AdminDashboardLocale,
): string {
  if (flow.statusCode !== undefined) {
    return text(locale, `HTTP ${flow.statusCode}`, `응답 코드 ${flow.statusCode}`);
  }

  return toRowStatus(flow.status, locale).statusLabel;
}

function toPlatformLabel(value: AdminFailedFlow["platform"]): string {
  return value?.toUpperCase() ?? "-";
}

function toDeviceInstallIdLabel(value: string | undefined): string {
  if (value === undefined || value.length === 0) {
    return "-";
  }

  if (value.length <= 14) {
    return value;
  }

  return `${value.slice(0, 8)}…${value.slice(-4)}`;
}

function toEventLabel(value: string | undefined): string {
  return value ?? "-";
}

function buildRequestEvidenceSummary(flow: AdminFailedFlow, locale: AdminDashboardLocale): string {
  const requestScope =
    flow.requestId ??
    flow.correlationId ??
    flow.jobId ??
    flow.deletionJobId ??
    text(locale, "unknown trace", "추적 키 없음");
  const statusSummary = toRequestEvidenceStatusSummary(flow, locale);
  return locale === "en"
    ? `${flow.useCase} failed as ${flow.signalKey}.${flow.stage} with ${flow.errorCode} (${statusSummary}). Primary trace: ${requestScope}.`
    : `${flow.useCase}가 ${flow.signalKey}.${flow.stage} 단계에서 ${flow.errorCode} (${statusSummary})로 실패했습니다. 주요 추적 키: ${requestScope}.`;
}

function buildRequestEvidenceTraceKeys(
  flow: AdminFailedFlow,
  drilldownTarget: AdminFailedFlowDrilldownTarget,
  locale: AdminDashboardLocale,
): FailedFlowTraceKeyViewModel[] {
  const keys: FailedFlowTraceKeyViewModel[] = [];

  if (flow.requestId) {
    keys.push({
      id: "requestId",
      label: text(locale, "Request", "요청"),
      value: flow.requestId,
      href: drilldownTarget.request,
    });
  }

  if (flow.correlationId) {
    keys.push({
      id: "correlationId",
      label: text(locale, "Correlation", "상관관계"),
      value: flow.correlationId,
      href: drilldownTarget.correlation,
    });
  }

  if (flow.userIdHash) {
    keys.push({
      id: "userIdHash",
      label: text(locale, "User", "사용자"),
      value: flow.userIdHash,
      href: drilldownTarget.userSummary,
    });
  }

  const jobValue = flow.jobId ?? flow.deletionJobId;
  if (jobValue) {
    keys.push({
      id: "jobId",
      label: text(locale, "Job", "작업"),
      value: jobValue,
      href: drilldownTarget.jobs,
    });
  }

  if (flow.petIdHash) {
    keys.push({
      id: "petIdHash",
      label: text(locale, "Pet", "펫"),
      value: flow.petIdHash,
    });
  }

  if (flow.recordId) {
    keys.push({
      id: "recordId",
      label: text(locale, "Record", "기록"),
      value: flow.recordId,
    });
  }

  if (flow.receiptId) {
    keys.push({
      id: "receiptId",
      label: text(locale, "Receipt", "영수증"),
      value: flow.receiptId,
    });
  }

  if (flow.fileId) {
    keys.push({
      id: "fileId",
      label: text(locale, "File", "파일"),
      value: flow.fileId,
    });
  }

  return keys;
}

function buildRequestEvidenceLogAvailabilitySummary(
  flow: AdminFailedFlow,
  drilldownTarget: AdminFailedFlowDrilldownTarget,
  requestLogsUrl: string | undefined,
  locale: AdminDashboardLocale,
): string {
  if (requestLogsUrl) {
    return text(
      locale,
      "Cloud Logging request deep link is available for this requestId. Confirm the stable error code, HTTP status, and exact server-side failure point there first.",
      "이 requestId에는 Cloud Logging 요청 바로가기가 있습니다. 먼저 안정 오류 코드, 응답 상태, 정확한 서버 측 실패 지점을 확인하세요.",
    );
  }

  if (flow.requestId) {
    if (drilldownTarget.request) {
      return text(
        locale,
        "This row has a requestId, but the deployed dashboard bundle is missing Cloud Logging observability config. Re-open the request scope here and restore observability env to re-enable the deep link.",
        "이 행에는 requestId가 있지만 배포된 대시보드 번들에 Cloud Logging 관찰성 설정이 없습니다. 여기서 요청 범위를 다시 열고 관찰성 환경 설정을 복구해 바로가기를 다시 활성화하세요.",
      );
    }

    return text(
      locale,
      "This row has a requestId, but the Cloud Logging deep link is unavailable because the deployed dashboard bundle is missing observability config.",
      "이 행에는 requestId가 있지만 배포된 대시보드 번들에 관찰성 설정이 없어 Cloud Logging 바로가기를 만들 수 없습니다.",
    );
  }

  if (flow.correlationId) {
    return text(
      locale,
      "No requestId was captured on this row. Re-scope by correlation first, then inspect the related request or worker trail.",
      "이 행에는 requestId가 수집되지 않았습니다. 먼저 상관관계 기준으로 범위를 다시 잡고 관련 요청 또는 작업자 추적을 확인하세요.",
    );
  }

  return text(
    locale,
    "No requestId or correlationId was captured on this row. Stay on the incident headline and compare nearby release or device failures.",
    "이 행에는 requestId와 correlationId가 모두 없습니다. 사고 헤드라인을 기준으로 주변 릴리스 또는 기기 실패를 비교하세요.",
  );
}

function buildRequestEvidence(
  flow: AdminFailedFlow,
  drilldownTarget: AdminFailedFlowDrilldownTarget,
  requestLogsUrl: string | undefined,
  locale: AdminDashboardLocale,
): FailedFlowRequestEvidenceViewModel {
  return {
    summary: buildRequestEvidenceSummary(flow, locale),
    logAvailabilitySummary: buildRequestEvidenceLogAvailabilitySummary(
      flow,
      drilldownTarget,
      requestLogsUrl,
      locale,
    ),
    traceKeys: buildRequestEvidenceTraceKeys(flow, drilldownTarget, locale),
  };
}

function buildIncidentSummary(flow: AdminFailedFlow, locale: AdminDashboardLocale): string {
  if (locale === "en") {
    if (flow.signalKey === "account_deletion" && flow.stage === "account_deletion_process") {
      return "The flow failed during account deletion worker processing or terminal cleanup.";
    }

    if (flow.signalKey === "account_deletion" && flow.stage === "account_deletion_request") {
      return "The flow failed while creating the account deletion request.";
    }

    if (flow.signalKey === "account_deletion" && flow.stage === "account_deletion_reauth") {
      return "The flow failed during the reauthentication check before account deletion.";
    }

    if (flow.signalKey === "pet_profile" && flow.stage === "pet_create") {
      return "The user failed during pet create.";
    }

    if (flow.signalKey === "pet_profile" && flow.stage === "pet_update") {
      return "The flow failed during pet profile update.";
    }

    if (flow.signalKey === "pet_profile" && flow.stage === "pet_delete") {
      return "The flow failed during pet delete.";
    }

    if (flow.signalKey === "pet_profile" && flow.stage === "pet_photo_confirm") {
      return "The flow failed during pet profile photo confirm.";
    }

    if (flow.signalKey === "community_post" && flow.stage === "community_image_upload") {
      return "The flow failed during community post image upload.";
    }

    if (flow.signalKey === "community_post" && flow.stage === "community_post_create") {
      return "The flow failed during community post create.";
    }

    if (flow.signalKey === "community_post" && flow.stage === "community_post_update") {
      return "The flow failed during community post update.";
    }

    if (flow.signalKey === "community_post" && flow.stage === "community_post_delete") {
      return "The flow failed during community post delete.";
    }

    if (flow.signalKey === "community_post" && flow.stage === "community_comment_create") {
      return "The flow failed during community comment create.";
    }

    if (flow.signalKey === "daily_record" && flow.stage === "today_status_save") {
      return "The flow failed during today quick status save.";
    }

    if (flow.signalKey === "daily_record" && flow.stage === "daily_draft_create") {
      return "The flow failed during daily AI draft creation.";
    }

    if (flow.signalKey === "daily_record" && flow.stage === "daily_record_save") {
      return "The flow failed during confirmed daily record save.";
    }

    if (flow.signalKey === "daily_media" && flow.stage === "record_update") {
      return "The flow failed during daily media attach or delete.";
    }

    if (flow.signalKey === "health_record" && flow.stage === "health_file_upload") {
      return "The flow failed during health source temp upload.";
    }

    if (flow.signalKey === "health_record" && flow.stage === "health_file_confirm") {
      return "The flow failed during health source file confirm.";
    }

    if (flow.signalKey === "health_record" && flow.stage === "health_analysis_process") {
      return "The flow failed during health AI analysis queue or provider processing.";
    }

    if (flow.signalKey === "health_record" && flow.stage === "health_record_save") {
      return "The flow failed during confirmed health record save.";
    }

    if (flow.signalKey === "receipt_record" && flow.stage === "receipt_extract") {
      return "The flow failed during receipt AI extraction draft.";
    }

    if (flow.signalKey === "receipt_record" && flow.stage === "receipt_save") {
      return "The flow failed during confirmed receipt save.";
    }

    if (flow.signalKey === "receipt_record" && flow.stage === "receipt_category_create") {
      return "The flow failed during receipt custom category create.";
    }

    if (flow.signalKey === "receipt_record" && flow.stage === "receipt_category_update") {
      return "The flow failed during receipt custom category update.";
    }

    if (flow.signalKey === "receipt_record" && flow.stage === "receipt_category_delete") {
      return "The flow failed during receipt custom category delete.";
    }

    if (flow.signalKey === "social_profile" && flow.stage === "social_profile_me") {
      return "The flow failed during my public profile lookup.";
    }

    if (flow.signalKey === "social_profile" && flow.stage === "social_profile_view") {
      return "The flow failed during target public profile lookup.";
    }

    if (flow.signalKey === "social_profile" && flow.stage === "social_profile_update") {
      return "The flow failed during my public profile update.";
    }

    if (flow.signalKey === "social_profile" && flow.stage === "social_follow_create") {
      return "The flow failed during follow create.";
    }

    if (flow.signalKey === "social_profile" && flow.stage === "social_follow_delete") {
      return "The flow failed during follow delete.";
    }

    if (flow.signalKey === "social_profile" && flow.stage === "social_follow_check") {
      return "The flow failed during follow relationship check.";
    }

    if (flow.signalKey === "social_profile" && flow.stage === "social_follow_count") {
      return "The flow failed during follow count lookup.";
    }

    if (flow.signalKey === "chat_room" && flow.stage === "chat_direct_room_list") {
      return "The flow failed during direct chat room list lookup.";
    }

    if (flow.signalKey === "chat_room" && flow.stage === "chat_direct_room_open") {
      return "The flow failed during direct chat room open.";
    }

    if (flow.signalKey === "chat_room" && flow.stage === "chat_direct_message_list") {
      return "The flow failed during direct chat message list lookup.";
    }

    if (flow.signalKey === "chat_room" && flow.stage === "chat_direct_message_send") {
      return "The flow failed during direct chat message send.";
    }

    if (flow.signalKey === "chat_room" && flow.stage === "chat_group_room_list") {
      return "The flow failed during group chat room list lookup.";
    }

    if (flow.signalKey === "chat_room" && flow.stage === "chat_group_room_create") {
      return "The flow failed during group chat room create.";
    }

    if (flow.signalKey === "chat_room" && flow.stage === "chat_group_room_join") {
      return "The flow failed during group chat room join.";
    }

    if (flow.signalKey === "chat_room" && flow.stage === "chat_group_room_leave") {
      return "The flow failed during group chat room leave.";
    }

    if (flow.signalKey === "chat_room" && flow.stage === "chat_group_message_list") {
      return "The flow failed during group chat message list lookup.";
    }

    if (flow.signalKey === "chat_room" && flow.stage === "chat_group_message_send") {
      return "The flow failed during group chat message send.";
    }

    if (flow.signalKey === "medication_schedule" && flow.stage === "medication_list") {
      return "The flow failed during medication schedule list lookup.";
    }

    if (flow.signalKey === "medication_schedule" && flow.stage === "medication_detail") {
      return "The flow failed during medication schedule detail lookup.";
    }

    if (flow.signalKey === "medication_schedule" && flow.stage === "medication_create") {
      return "The flow failed during medication schedule create.";
    }

    if (flow.signalKey === "medication_schedule" && flow.stage === "medication_update") {
      return "The flow failed during medication schedule update.";
    }

    if (flow.signalKey === "medication_schedule" && flow.stage === "medication_delete") {
      return "The flow failed during medication schedule delete.";
    }

    if (flow.signalKey === "medication_schedule" && flow.stage === "medication_complete") {
      return "The flow failed during medication completion.";
    }

    if (flow.signalKey === "medication_schedule" && flow.stage === "medication_skip") {
      return "The flow failed during medication skip.";
    }

    if (flow.signalKey === "medication_schedule" && flow.stage === "medication_snooze") {
      return "The flow failed during medication snooze.";
    }

    if (flow.signalKey === "ride_request" && flow.stage === "ride_request_list") {
      return "The flow failed during ride request list lookup.";
    }

    if (flow.signalKey === "ride_request" && flow.stage === "ride_request_detail") {
      return "The flow failed during ride request detail lookup.";
    }

    if (flow.signalKey === "ride_request" && flow.stage === "ride_request_create") {
      return "The flow failed during ride request create.";
    }

    if (flow.signalKey === "ride_request" && flow.stage === "ride_request_update") {
      return "The flow failed during ride request update.";
    }

    if (flow.signalKey === "ride_request" && flow.stage === "ride_request_delete") {
      return "The flow failed during ride request delete.";
    }

    if (flow.signalKey === "ride_request" && flow.stage === "ride_request_submit") {
      return "The flow failed during ride request submit.";
    }

    if (flow.signalKey === "ride_request" && flow.stage === "ride_request_cancel") {
      return "The flow failed during ride request cancel.";
    }

    if (flow.signalKey === "file_lifecycle" && flow.stage === "file_cleanup") {
      return "The flow failed during file cleanup scheduler or worker execution.";
    }

    if (flow.signalKey === "auth_session" && flow.stage === "social_login") {
      return "The flow failed during social login.";
    }

    if (flow.signalKey === "auth_session" && flow.stage === "session_restore") {
      return "The flow failed during session restore.";
    }

    if (flow.signalKey === "auth_session" && flow.stage === "terms_consent_accept") {
      return "The flow failed during terms consent acceptance.";
    }

    if (flow.signalKey === "auth_session" && flow.stage === "auth_logout") {
      return "The flow failed during logout handling.";
    }

    if (flow.signalKey === "auth_session" && flow.stage === "auth_provider_link") {
      return "The flow failed during auth provider link.";
    }

    if (flow.signalKey === "auth_session" && flow.stage === "auth_provider_unlink") {
      return "The flow failed during auth provider unlink.";
    }

    return `This is a failed flow for ${flow.feature} / ${flow.useCase}.`;
  }

  if (
    flow.signalKey === "account_deletion" &&
    (flow.stage === "account_deletion_reauth" ||
      (flow.stage === "account_deletion_request" && flow.errorCode === "ACCOUNT_DELETION_REAUTH_REQUIRED"))
  ) {
    return "계정 삭제 전 최근 재인증 확인 단계에서 실패했습니다.";
  }

  if (flow.signalKey === "account_deletion" && flow.stage === "account_deletion_request") {
    return "사용자 계정 삭제 요청 생성 단계에서 실패했습니다.";
  }

  if (flow.signalKey === "account_deletion" && flow.stage === "account_deletion_process") {
    return "계정 삭제 작업자 처리 또는 최종 정리 단계에서 실패했습니다.";
  }

  if (flow.signalKey === "auth_session" && flow.stage === "social_login") {
    return "사용자 소셜 로그인 단계에서 실패했습니다.";
  }

  if (flow.signalKey === "auth_session" && flow.stage === "session_restore") {
    return "앱 세션 복구 단계에서 실패했습니다.";
  }

  if (flow.signalKey === "auth_session" && flow.stage === "terms_consent_accept") {
    return "약관 동의 저장 단계에서 실패했습니다.";
  }

  if (flow.signalKey === "auth_session" && flow.stage === "auth_logout") {
    return "사용자 로그아웃 처리 단계에서 실패했습니다.";
  }

  if (flow.signalKey === "auth_session" && flow.stage === "auth_provider_link") {
    return "인증 공급자 연결 단계에서 실패했습니다.";
  }

  if (flow.signalKey === "auth_session" && flow.stage === "auth_provider_unlink") {
    return "인증 공급자 연결 해제 단계에서 실패했습니다.";
  }

  if (flow.signalKey === "pet_profile" && flow.stage === "pet_create") {
    return "사용자가 반려동물 등록 단계에서 실패했습니다.";
  }

  if (flow.signalKey === "pet_profile" && flow.stage === "pet_update") {
    return "반려동물 프로필 수정 단계에서 실패했습니다.";
  }

  if (flow.signalKey === "pet_profile" && flow.stage === "pet_delete") {
    return "반려동물 삭제 단계에서 실패했습니다.";
  }

  if (flow.signalKey === "pet_profile" && flow.stage === "pet_photo_confirm") {
    return "반려동물 프로필 사진 확인 단계에서 실패했습니다.";
  }

  if (flow.signalKey === "community_post" && flow.stage === "community_image_upload") {
    return "커뮤니티 게시물 이미지 업로드 단계에서 실패했습니다.";
  }

  if (flow.signalKey === "community_post" && flow.stage === "community_post_create") {
    return "커뮤니티 게시물 생성 단계에서 실패했습니다.";
  }

  if (flow.signalKey === "community_post" && flow.stage === "community_post_update") {
    return "커뮤니티 게시물 수정 단계에서 실패했습니다.";
  }

  if (flow.signalKey === "community_post" && flow.stage === "community_post_delete") {
    return "커뮤니티 게시물 삭제 단계에서 실패했습니다.";
  }

  if (flow.signalKey === "community_post" && flow.stage === "community_comment_create") {
    return "커뮤니티 댓글 생성 단계에서 실패했습니다.";
  }

  if (flow.signalKey === "daily_record" && flow.stage === "today_status_save") {
    return "오늘 상태 저장 단계에서 실패했습니다.";
  }

  if (flow.signalKey === "daily_record" && flow.stage === "daily_draft_create") {
    return "일상 기록 AI 초안 생성 단계에서 실패했습니다.";
  }

  if (flow.signalKey === "daily_record" && flow.stage === "daily_record_save") {
    return "확정 일상 기록 저장 단계에서 실패했습니다.";
  }

  if (flow.signalKey === "daily_media" && flow.stage === "record_update") {
    return "일상 기록 미디어 첨부 또는 삭제 단계에서 실패했습니다.";
  }

  if (flow.signalKey === "health_record" && flow.stage === "health_file_upload") {
    return "건강 기록 원본 파일 임시 업로드 단계에서 실패했습니다.";
  }

  if (flow.signalKey === "health_record" && flow.stage === "health_file_confirm") {
    return "건강 기록 원본 파일 확인 단계에서 실패했습니다.";
  }

  if (flow.signalKey === "health_record" && flow.stage === "health_analysis_process") {
    return "건강 기록 AI 분석 대기열 또는 제공자 처리 단계에서 실패했습니다.";
  }

  if (flow.signalKey === "health_record" && flow.stage === "health_record_save") {
    return "확정 건강 기록 저장 단계에서 실패했습니다.";
  }

  if (flow.signalKey === "receipt_record" && flow.stage === "receipt_extract") {
    return "영수증 AI 추출 초안 단계에서 실패했습니다.";
  }

  if (flow.signalKey === "receipt_record" && flow.stage === "receipt_save") {
    return "확정 영수증 저장 단계에서 실패했습니다.";
  }

  if (flow.signalKey === "receipt_record" && flow.stage === "receipt_category_create") {
    return "영수증 커스텀 카테고리 생성 단계에서 실패했습니다.";
  }

  if (flow.signalKey === "receipt_record" && flow.stage === "receipt_category_update") {
    return "영수증 커스텀 카테고리 수정 단계에서 실패했습니다.";
  }

  if (flow.signalKey === "receipt_record" && flow.stage === "receipt_category_delete") {
    return "영수증 커스텀 카테고리 삭제 단계에서 실패했습니다.";
  }

  if (flow.signalKey === "social_profile" && flow.stage === "social_profile_me") {
    return "내 공개 프로필 조회 단계에서 실패했습니다.";
  }

  if (flow.signalKey === "social_profile" && flow.stage === "social_profile_view") {
    return "다른 사용자 공개 프로필 조회 단계에서 실패했습니다.";
  }

  if (flow.signalKey === "social_profile" && flow.stage === "social_profile_update") {
    return "내 공개 프로필 수정 단계에서 실패했습니다.";
  }

  if (flow.signalKey === "social_profile" && flow.stage === "social_follow_create") {
    return "사용자 팔로우 생성 단계에서 실패했습니다.";
  }

  if (flow.signalKey === "social_profile" && flow.stage === "social_follow_delete") {
    return "사용자 팔로우 해제 단계에서 실패했습니다.";
  }

  if (flow.signalKey === "social_profile" && flow.stage === "social_follow_check") {
    return "팔로우 관계 확인 단계에서 실패했습니다.";
  }

  if (flow.signalKey === "social_profile" && flow.stage === "social_follow_count") {
    return "팔로우 수 조회 단계에서 실패했습니다.";
  }

  if (flow.signalKey === "chat_room" && flow.stage === "chat_direct_room_list") {
    return "1:1 채팅방 목록 조회 단계에서 실패했습니다.";
  }

  if (flow.signalKey === "chat_room" && flow.stage === "chat_direct_room_open") {
    return "1:1 채팅방 열기 단계에서 실패했습니다.";
  }

  if (flow.signalKey === "chat_room" && flow.stage === "chat_direct_message_list") {
    return "1:1 채팅 메시지 목록 조회 단계에서 실패했습니다.";
  }

  if (flow.signalKey === "chat_room" && flow.stage === "chat_direct_message_send") {
    return "1:1 채팅 메시지 전송 단계에서 실패했습니다.";
  }

  if (flow.signalKey === "chat_room" && flow.stage === "chat_group_room_list") {
    return "그룹 채팅방 목록 조회 단계에서 실패했습니다.";
  }

  if (flow.signalKey === "chat_room" && flow.stage === "chat_group_room_create") {
    return "그룹 채팅방 생성 단계에서 실패했습니다.";
  }

  if (flow.signalKey === "chat_room" && flow.stage === "chat_group_room_join") {
    return "그룹 채팅방 참여 단계에서 실패했습니다.";
  }

  if (flow.signalKey === "chat_room" && flow.stage === "chat_group_room_leave") {
    return "그룹 채팅방 나가기 단계에서 실패했습니다.";
  }

  if (flow.signalKey === "chat_room" && flow.stage === "chat_group_message_list") {
    return "그룹 채팅 메시지 목록 조회 단계에서 실패했습니다.";
  }

  if (flow.signalKey === "chat_room" && flow.stage === "chat_group_message_send") {
    return "그룹 채팅 메시지 전송 단계에서 실패했습니다.";
  }

  if (flow.signalKey === "medication_schedule" && flow.stage === "medication_list") {
    return "복약 일정 목록 조회 단계에서 실패했습니다.";
  }

  if (flow.signalKey === "medication_schedule" && flow.stage === "medication_detail") {
    return "복약 일정 상세 조회 단계에서 실패했습니다.";
  }

  if (flow.signalKey === "medication_schedule" && flow.stage === "medication_create") {
    return "복약 일정 생성 단계에서 실패했습니다.";
  }

  if (flow.signalKey === "medication_schedule" && flow.stage === "medication_update") {
    return "복약 일정 수정 단계에서 실패했습니다.";
  }

  if (flow.signalKey === "medication_schedule" && flow.stage === "medication_delete") {
    return "복약 일정 삭제 단계에서 실패했습니다.";
  }

  if (flow.signalKey === "medication_schedule" && flow.stage === "medication_complete") {
    return "복약 완료 처리 단계에서 실패했습니다.";
  }

  if (flow.signalKey === "medication_schedule" && flow.stage === "medication_skip") {
    return "복약 건너뛰기 처리 단계에서 실패했습니다.";
  }

  if (flow.signalKey === "medication_schedule" && flow.stage === "medication_snooze") {
    return "복약 미루기 처리 단계에서 실패했습니다.";
  }

  if (flow.signalKey === "ride_request" && flow.stage === "ride_request_list") {
    return "이동 지원 요청 목록 조회 단계에서 실패했습니다.";
  }

  if (flow.signalKey === "ride_request" && flow.stage === "ride_request_detail") {
    return "이동 지원 요청 상세 조회 단계에서 실패했습니다.";
  }

  if (flow.signalKey === "ride_request" && flow.stage === "ride_request_create") {
    return "이동 지원 요청 생성 단계에서 실패했습니다.";
  }

  if (flow.signalKey === "ride_request" && flow.stage === "ride_request_update") {
    return "이동 지원 요청 수정 단계에서 실패했습니다.";
  }

  if (flow.signalKey === "ride_request" && flow.stage === "ride_request_delete") {
    return "이동 지원 요청 삭제 단계에서 실패했습니다.";
  }

  if (flow.signalKey === "ride_request" && flow.stage === "ride_request_submit") {
    return "이동 지원 요청 제출 단계에서 실패했습니다.";
  }

  if (flow.signalKey === "ride_request" && flow.stage === "ride_request_cancel") {
    return "이동 지원 요청 취소 단계에서 실패했습니다.";
  }

  if (flow.signalKey === "file_lifecycle" && flow.stage === "file_cleanup") {
    return "파일 정리 스케줄러 또는 작업자 단계에서 실패했습니다.";
  }

  return `${flow.feature} / ${flow.useCase} 실패 흐름입니다.`;
}

function buildFirstCheckLabel(flow: AdminFailedFlow, locale: AdminDashboardLocale): string {
  if (locale === "en") {
    if (flow.signalKey === "auth_session" && flow.stage === "terms_consent_accept") {
      return "Open the POST /api/users/me/terms-consents requestId log first, then confirm required acceptedTermIds, restored user session ownership, idToken verification, and the session restore that runs immediately after terms are saved.";
    }

    if (flow.signalKey === "social_profile" && flow.stage === "social_profile_me") {
      return "Open the GET /api/profiles/me requestId log first, then confirm current-user session ownership, profile projection lookup, and signed avatar fallback handling.";
    }

    if (flow.signalKey === "social_profile" && flow.stage === "social_profile_view") {
      return "Open the GET /api/profiles/:userId requestId log first, then confirm target user visibility, public pet exposure rules, and follow-context projection lookup.";
    }

    if (flow.signalKey === "social_profile" && flow.stage === "social_profile_update") {
      return "Open the PATCH /api/profiles/me requestId log first, then confirm profile field validation, avatar or bio mutation handling, and owner-scoped profile persistence.";
    }

    if (flow.signalKey === "social_profile" && flow.stage === "social_follow_create") {
      return "Open the POST /api/follows requestId log first, then confirm target user ownership rules, duplicate follow protection, and follow edge persistence.";
    }

    if (flow.signalKey === "social_profile" && flow.stage === "social_follow_delete") {
      return "Open the DELETE /api/follows/:targetUserId requestId log first, then confirm target user linkage, already-unfollowed fallback handling, and follow edge removal.";
    }

    if (flow.signalKey === "social_profile" && flow.stage === "social_follow_check") {
      return "Open the GET /api/follows/check requestId log first, then confirm target user lookup, viewer session identity, and follow relationship read-model resolution.";
    }

    if (flow.signalKey === "social_profile" && flow.stage === "social_follow_count") {
      return "Open the GET /api/follows/count requestId log first, then confirm target user lookup and follower or following count aggregation.";
    }

    if (flow.signalKey === "chat_room" && flow.stage === "chat_direct_room_list") {
      return "Open the GET /api/chat/rooms requestId log first, then confirm viewer room membership scope, pagination cursor handling, and last-message projection lookup.";
    }

    if (flow.signalKey === "chat_room" && flow.stage === "chat_direct_room_open") {
      return "Open the POST /api/chat/rooms requestId log first, then confirm peer user lookup, existing-room reuse, and direct room create or open persistence.";
    }

    if (flow.signalKey === "chat_room" && flow.stage === "chat_direct_message_list") {
      return "Open the GET /api/chat/rooms/:roomId/messages requestId log first, then confirm viewer membership, cursor paging, and message projection ordering.";
    }

    if (flow.signalKey === "chat_room" && flow.stage === "chat_direct_message_send") {
      return "Open the POST /api/chat/rooms/:roomId/messages requestId log first, then confirm room membership, clientMessageId dedupe handling, and message persistence.";
    }

    if (flow.signalKey === "chat_room" && flow.stage === "chat_group_room_list") {
      return "Open the GET /api/group/rooms requestId log first, then confirm viewer membership scope, room summary projection, and pagination handling.";
    }

    if (flow.signalKey === "chat_room" && flow.stage === "chat_group_room_create") {
      return "Open the POST /api/group/rooms requestId log first, then confirm group room title or member validation, initial membership creation, and room persistence.";
    }

    if (flow.signalKey === "chat_room" && flow.stage === "chat_group_room_join") {
      return "Open the POST /api/group/rooms/:roomId/join requestId log first, then confirm invite or visibility rules, duplicate membership protection, and membership persistence.";
    }

    if (flow.signalKey === "chat_room" && flow.stage === "chat_group_room_leave") {
      return "Open the POST /api/group/rooms/:roomId/leave requestId log first, then confirm membership existence, owner-leave protection, and membership removal.";
    }

    if (flow.signalKey === "chat_room" && flow.stage === "chat_group_message_list") {
      return "Open the GET /api/group/rooms/:roomId/messages requestId log first, then confirm viewer membership, cursor paging, and group message ordering.";
    }

    if (flow.signalKey === "chat_room" && flow.stage === "chat_group_message_send") {
      return "Open the POST /api/group/rooms/:roomId/messages requestId log first, then confirm room membership, clientMessageId dedupe handling, and group message persistence.";
    }

    if (flow.signalKey === "medication_schedule" && flow.stage === "medication_list") {
      return "Open the GET /api/medications requestId log first, then confirm pet ownership scope, date or status filters, and medication projection lookup.";
    }

    if (flow.signalKey === "medication_schedule" && flow.stage === "medication_detail") {
      return "Open the GET /api/medications/:medicationId requestId log first, then confirm medication ownership, linked pet scope, and detail projection lookup.";
    }

    if (flow.signalKey === "medication_schedule" && flow.stage === "medication_create") {
      return "Open the POST /api/medications requestId log first, then confirm pet ownership, schedule validation, and recurrence or reminder persistence.";
    }

    if (flow.signalKey === "medication_schedule" && flow.stage === "medication_update") {
      return "Open the PATCH /api/medications/:medicationId requestId log first, then confirm medication ownership, partial update validation, and schedule mutation persistence.";
    }

    if (flow.signalKey === "medication_schedule" && flow.stage === "medication_delete") {
      return "Open the DELETE /api/medications/:medicationId requestId log first, then confirm medication ownership and already-deleted fallback handling.";
    }

    if (flow.signalKey === "medication_schedule" && flow.stage === "medication_complete") {
      return "Open the POST /api/medications/:medicationId/complete requestId log first, then confirm medication ownership, occurrence completion rules, and completion persistence.";
    }

    if (flow.signalKey === "medication_schedule" && flow.stage === "medication_skip") {
      return "Open the POST /api/medications/:medicationId/skip requestId log first, then confirm medication ownership, skip reason or occurrence rules, and skip persistence.";
    }

    if (flow.signalKey === "medication_schedule" && flow.stage === "medication_snooze") {
      return "Open the POST /api/medications/:medicationId/snooze requestId log first, then confirm medication ownership, snooze window validation, and next-occurrence reschedule handling.";
    }

    if (flow.signalKey === "ride_request" && flow.stage === "ride_request_list") {
      return "Open the GET /api/ride-requests requestId log first, then confirm pet ownership scope, status filtering, and ride request projection lookup.";
    }

    if (flow.signalKey === "ride_request" && flow.stage === "ride_request_detail") {
      return "Open the GET /api/ride-requests/:rideRequestId requestId log first, then confirm ride request ownership, linked pet scope, and detail projection lookup.";
    }

    if (flow.signalKey === "ride_request" && flow.stage === "ride_request_create") {
      return "Open the POST /api/ride-requests requestId log first, then confirm pet ownership, pickup or destination validation, and ride request persistence.";
    }

    if (flow.signalKey === "ride_request" && flow.stage === "ride_request_update") {
      return "Open the PATCH /api/ride-requests/:rideRequestId requestId log first, then confirm ride request ownership, mutable-field validation, and update persistence.";
    }

    if (flow.signalKey === "ride_request" && flow.stage === "ride_request_delete") {
      return "Open the DELETE /api/ride-requests/:rideRequestId requestId log first, then confirm ride request ownership and already-deleted fallback handling.";
    }

    if (flow.signalKey === "ride_request" && flow.stage === "ride_request_submit") {
      return "Open the POST /api/ride-requests/:rideRequestId/submit requestId log first, then confirm ride request ownership, submit eligibility, and state transition persistence.";
    }

    if (flow.signalKey === "ride_request" && flow.stage === "ride_request_cancel") {
      return "Open the POST /api/ride-requests/:rideRequestId/cancel requestId log first, then confirm ride request ownership, cancel eligibility, and state transition persistence.";
    }

    if (flow.requestId) {
      return "Open the requestId-scoped API or worker log first, then confirm the stable error code, HTTP status, ownership scope, and linked entity trace before moving to related screens.";
    }

    if (flow.correlationId) {
      return "Re-scope by correlation first, then inspect the related request or worker trail before comparing release or device failures.";
    }

    return "Start from the incident headline and nearby release or device failures, because this row does not carry a requestId.";
  }

  if (
    flow.signalKey === "account_deletion" &&
    (flow.stage === "account_deletion_reauth" ||
      (flow.stage === "account_deletion_request" && flow.errorCode === "ACCOUNT_DELETION_REAUTH_REQUIRED"))
  ) {
    return "먼저 POST /api/users/me/deletion-jobs requestId 로그를 열고 Firebase authenticatedAt 5분 구간, 공급자 재인증 갱신 여부, 앱의 재인증 직후 삭제 재시도 경로를 확인하세요.";
  }

  if (flow.signalKey === "account_deletion" && flow.stage === "account_deletion_request") {
    return "먼저 POST /api/users/me/deletion-jobs requestId 로그를 열고 confirmationText=DELETE 검증, 소유 사용자 세션, 스케줄러 적재 결과와 생성된 삭제 작업 ID를 확인하세요.";
  }

  if (flow.signalKey === "account_deletion" && flow.stage === "account_deletion_process") {
    return "먼저 계정 삭제 작업 행과 POST /api/internal/users/account-deletion-jobs/process requestId 로그를 함께 열고 내부 토큰, 소유 사용자 범위 작업 조회, 로컬 데이터 정리, Firebase 자격 증명 최종 정리 실패 지점을 순서대로 확인하세요.";
  }

  if (flow.signalKey === "auth_session" && flow.stage === "social_login") {
    return "먼저 POST /api/auth/social-login requestId 로그를 열고 공급자 자격 증명, Firebase 토큰 검증, 신규 또는 기존 계정 매핑 오류를 확인하세요.";
  }

  if (flow.signalKey === "auth_session" && flow.stage === "session_restore") {
    return "먼저 GET /api/auth/session requestId 로그를 열고 만료된 토큰, 누락된 기기 또는 설치본 헤더, 세션 복구 오류를 확인하세요.";
  }

  if (flow.signalKey === "auth_session" && flow.stage === "terms_consent_accept") {
    return "먼저 POST /api/users/me/terms-consents requestId 로그를 열고 필수 약관 acceptedTermIds 포함 여부, 복구된 사용자 세션, idToken 검증, 약관 저장 직후 세션 재조회 실패를 순서대로 확인하세요.";
  }

  if (flow.signalKey === "auth_session" && flow.stage === "auth_logout") {
    return "먼저 POST /api/auth/logout requestId 로그를 열고 현재 세션 정리, 공급자 연결 해제 전제조건, 토큰 무효화 오류를 확인하세요.";
  }

  if (flow.signalKey === "auth_session" && flow.stage === "auth_provider_link") {
    return "먼저 POST /api/auth/providers/link requestId 로그를 열고 재인증 요구, 공급자 중복 연결, 계정 병합 오류를 확인하세요.";
  }

  if (flow.signalKey === "auth_session" && flow.stage === "auth_provider_unlink") {
    return "먼저 DELETE /api/auth/providers/:provider requestId 로그를 열고 마지막 로그인 수단 보호, 재인증 요구, 공급자 소유권 오류를 확인하세요.";
  }

  if (flow.signalKey === "social_profile" && flow.stage === "social_profile_me") {
    return "먼저 GET /api/profiles/me requestId 로그를 열고 현재 사용자 세션 소유권, 프로필 프로젝션 조회, 서명 avatar fallback 처리 오류를 확인하세요.";
  }

  if (flow.signalKey === "social_profile" && flow.stage === "social_profile_view") {
    return "먼저 GET /api/profiles/:userId requestId 로그를 열고 대상 사용자 공개 범위, public pet 노출 규칙, 팔로우 문맥 프로젝션 조회를 확인하세요.";
  }

  if (flow.signalKey === "social_profile" && flow.stage === "social_profile_update") {
    return "먼저 PATCH /api/profiles/me requestId 로그를 열고 프로필 필드 검증, avatar 또는 bio 변경 처리, 소유자 범위 프로필 저장을 확인하세요.";
  }

  if (flow.signalKey === "social_profile" && flow.stage === "social_follow_create") {
    return "먼저 POST /api/follows requestId 로그를 열고 대상 사용자 연결 규칙, 중복 팔로우 보호, 팔로우 edge 저장을 확인하세요.";
  }

  if (flow.signalKey === "social_profile" && flow.stage === "social_follow_delete") {
    return "먼저 DELETE /api/follows/:targetUserId requestId 로그를 열고 대상 사용자 연결, 이미 언팔로우된 상태 fallback, 팔로우 edge 제거를 확인하세요.";
  }

  if (flow.signalKey === "social_profile" && flow.stage === "social_follow_check") {
    return "먼저 GET /api/follows/check requestId 로그를 열고 대상 사용자 조회, viewer 세션 식별, 팔로우 관계 read model 해석을 확인하세요.";
  }

  if (flow.signalKey === "social_profile" && flow.stage === "social_follow_count") {
    return "먼저 GET /api/follows/count requestId 로그를 열고 대상 사용자 조회와 follower 또는 following count 집계를 확인하세요.";
  }

  if (flow.signalKey === "chat_room" && flow.stage === "chat_direct_room_list") {
    return "먼저 GET /api/chat/rooms requestId 로그를 열고 viewer 채팅방 membership 범위, cursor paging, 마지막 메시지 프로젝션 조회를 확인하세요.";
  }

  if (flow.signalKey === "chat_room" && flow.stage === "chat_direct_room_open") {
    return "먼저 POST /api/chat/rooms requestId 로그를 열고 peer 사용자 조회, 기존 채팅방 재사용, direct room 생성 또는 열기 저장을 확인하세요.";
  }

  if (flow.signalKey === "chat_room" && flow.stage === "chat_direct_message_list") {
    return "먼저 GET /api/chat/rooms/:roomId/messages requestId 로그를 열고 viewer membership, cursor paging, 메시지 정렬 프로젝션을 확인하세요.";
  }

  if (flow.signalKey === "chat_room" && flow.stage === "chat_direct_message_send") {
    return "먼저 POST /api/chat/rooms/:roomId/messages requestId 로그를 열고 채팅방 membership, clientMessageId dedupe 처리, 메시지 저장을 확인하세요.";
  }

  if (flow.signalKey === "chat_room" && flow.stage === "chat_group_room_list") {
    return "먼저 GET /api/group/rooms requestId 로그를 열고 viewer membership 범위, 그룹 채팅방 요약 프로젝션, pagination 처리를 확인하세요.";
  }

  if (flow.signalKey === "chat_room" && flow.stage === "chat_group_room_create") {
    return "먼저 POST /api/group/rooms requestId 로그를 열고 그룹 채팅방 제목 또는 멤버 검증, 초기 membership 생성, 채팅방 저장을 확인하세요.";
  }

  if (flow.signalKey === "chat_room" && flow.stage === "chat_group_room_join") {
    return "먼저 POST /api/group/rooms/:roomId/join requestId 로그를 열고 초대 또는 공개 범위 규칙, 중복 membership 보호, membership 저장을 확인하세요.";
  }

  if (flow.signalKey === "chat_room" && flow.stage === "chat_group_room_leave") {
    return "먼저 POST /api/group/rooms/:roomId/leave requestId 로그를 열고 membership 존재 여부, owner leave 보호, membership 제거를 확인하세요.";
  }

  if (flow.signalKey === "chat_room" && flow.stage === "chat_group_message_list") {
    return "먼저 GET /api/group/rooms/:roomId/messages requestId 로그를 열고 viewer membership, cursor paging, 그룹 메시지 정렬을 확인하세요.";
  }

  if (flow.signalKey === "chat_room" && flow.stage === "chat_group_message_send") {
    return "먼저 POST /api/group/rooms/:roomId/messages requestId 로그를 열고 채팅방 membership, clientMessageId dedupe 처리, 그룹 메시지 저장을 확인하세요.";
  }

  if (flow.signalKey === "medication_schedule" && flow.stage === "medication_list") {
    return "먼저 GET /api/medications requestId 로그를 열고 반려동물 소유권 범위, 날짜 또는 상태 필터, 복약 일정 프로젝션 조회를 확인하세요.";
  }

  if (flow.signalKey === "medication_schedule" && flow.stage === "medication_detail") {
    return "먼저 GET /api/medications/:medicationId requestId 로그를 열고 복약 일정 소유권, 연결된 반려동물 범위, 상세 프로젝션 조회를 확인하세요.";
  }

  if (flow.signalKey === "medication_schedule" && flow.stage === "medication_create") {
    return "먼저 POST /api/medications requestId 로그를 열고 반려동물 소유권, 스케줄 검증, recurrence 또는 reminder 저장을 확인하세요.";
  }

  if (flow.signalKey === "medication_schedule" && flow.stage === "medication_update") {
    return "먼저 PATCH /api/medications/:medicationId requestId 로그를 열고 복약 일정 소유권, 부분 수정 검증, 스케줄 변경 저장을 확인하세요.";
  }

  if (flow.signalKey === "medication_schedule" && flow.stage === "medication_delete") {
    return "먼저 DELETE /api/medications/:medicationId requestId 로그를 열고 복약 일정 소유권과 이미 삭제된 상태 fallback을 확인하세요.";
  }

  if (flow.signalKey === "medication_schedule" && flow.stage === "medication_complete") {
    return "먼저 POST /api/medications/:medicationId/complete requestId 로그를 열고 복약 일정 소유권, 복약 완료 규칙, 완료 상태 저장을 확인하세요.";
  }

  if (flow.signalKey === "medication_schedule" && flow.stage === "medication_skip") {
    return "먼저 POST /api/medications/:medicationId/skip requestId 로그를 열고 복약 일정 소유권, skip 규칙, 건너뛰기 상태 저장을 확인하세요.";
  }

  if (flow.signalKey === "medication_schedule" && flow.stage === "medication_snooze") {
    return "먼저 POST /api/medications/:medicationId/snooze requestId 로그를 열고 복약 일정 소유권, snooze 시간 검증, 다음 발생 재조정을 확인하세요.";
  }

  if (flow.signalKey === "ride_request" && flow.stage === "ride_request_list") {
    return "먼저 GET /api/ride-requests requestId 로그를 열고 반려동물 소유권 범위, 상태 필터, 이동 지원 요청 프로젝션 조회를 확인하세요.";
  }

  if (flow.signalKey === "ride_request" && flow.stage === "ride_request_detail") {
    return "먼저 GET /api/ride-requests/:rideRequestId requestId 로그를 열고 이동 지원 요청 소유권, 연결된 반려동물 범위, 상세 프로젝션 조회를 확인하세요.";
  }

  if (flow.signalKey === "ride_request" && flow.stage === "ride_request_create") {
    return "먼저 POST /api/ride-requests requestId 로그를 열고 반려동물 소유권, pickup 또는 destination 검증, 이동 지원 요청 저장을 확인하세요.";
  }

  if (flow.signalKey === "ride_request" && flow.stage === "ride_request_update") {
    return "먼저 PATCH /api/ride-requests/:rideRequestId requestId 로그를 열고 이동 지원 요청 소유권, 수정 가능한 필드 검증, 업데이트 저장을 확인하세요.";
  }

  if (flow.signalKey === "ride_request" && flow.stage === "ride_request_delete") {
    return "먼저 DELETE /api/ride-requests/:rideRequestId requestId 로그를 열고 이동 지원 요청 소유권과 이미 삭제된 상태 fallback을 확인하세요.";
  }

  if (flow.signalKey === "ride_request" && flow.stage === "ride_request_submit") {
    return "먼저 POST /api/ride-requests/:rideRequestId/submit requestId 로그를 열고 제출 가능 상태, 소유권, 상태 전이 저장을 확인하세요.";
  }

  if (flow.signalKey === "ride_request" && flow.stage === "ride_request_cancel") {
    return "먼저 POST /api/ride-requests/:rideRequestId/cancel requestId 로그를 열고 취소 가능 상태, 소유권, 상태 전이 저장을 확인하세요.";
  }

  if (flow.signalKey === "pet_profile" && flow.stage === "pet_create") {
    return "먼저 POST /api/pets requestId 로그를 열고 입력 검증, 소유권, 중복 입력, 프로필 정규화 오류를 확인하세요.";
  }

  if (flow.signalKey === "pet_profile" && flow.stage === "pet_update") {
    return "먼저 PATCH /api/pets/:petId requestId 로그를 열고 소유권, 부분 수정 검증, 추천 적용 충돌, 삭제된 반려동물 상태를 확인하세요.";
  }

  if (flow.signalKey === "pet_profile" && flow.stage === "pet_delete") {
    return "먼저 DELETE /api/pets/:petId requestId 로그를 열고 소유 사용자 범위, 연결 데이터 보호, 이미 삭제된 반려동물 처리 오류를 확인하세요.";
  }

  if (flow.signalKey === "pet_profile" && flow.stage === "pet_photo_confirm") {
    return "먼저 POST /api/files/:fileId/confirm requestId 로그를 열고 pet_profile_photo 용도, 파일 소유권, 누락된 파일 또는 링크 오류를 확인하세요.";
  }

  if (flow.signalKey === "community_post" && flow.stage === "community_image_upload") {
    return "먼저 POST /api/files requestId 로그를 열고 community_post_image 용도, 이미지 개수 제한, 파일 소유권, 저장소 저장 오류를 확인하세요.";
  }

  if (flow.signalKey === "community_post" && flow.stage === "community_post_create") {
    return "먼저 POST /api/community/posts requestId 로그를 열고 본문 길이 검증, community_post_image 확정 상태, 반려동물 문맥 연결, 게시물 이미지 정렬 오류를 확인하세요.";
  }

  if (flow.signalKey === "community_post" && flow.stage === "community_post_update") {
    return "먼저 PATCH /api/community/posts/:postId requestId 로그를 열고 게시물 소유권, 이미지 교체 또는 제거 규칙, 본문 정규화, soft delete 충돌을 확인하세요.";
  }

  if (flow.signalKey === "community_post" && flow.stage === "community_post_delete") {
    return "먼저 DELETE /api/community/posts/:postId requestId 로그를 열고 게시물 소유권, 연결된 community_post_image 정리 경로, 이미 삭제된 게시물 처리 오류를 확인하세요.";
  }

  if (flow.signalKey === "community_post" && flow.stage === "community_comment_create") {
    return "먼저 POST /api/community/posts/:postId/comments requestId 로그를 열고 게시물 접근 범위, 댓글 본문 검증, soft deleted post 충돌을 확인하세요.";
  }

  if (flow.signalKey === "daily_record" && flow.stage === "today_status_save") {
    return "먼저 PUT /api/records/today-status requestId 로그를 열고 반려동물 소유권, 상태 입력값 검증, 선택된 반려동물 동기화 오류를 확인하세요.";
  }

  if (flow.signalKey === "daily_record" && flow.stage === "daily_draft_create") {
    return "먼저 POST /api/record-drafts/daily-analysis requestId 로그를 열고 sourceText 누락, 일상 분석기 대체 경로, AI 작업 적재 오류를 확인하세요.";
  }

  if (flow.signalKey === "daily_record" && flow.stage === "daily_record_save") {
    return "먼저 POST/PATCH /api/records requestId 로그를 열고 초안 확정 입력값, recordedAt 정규화, 기록 소유권 또는 소프트 삭제 충돌을 확인하세요.";
  }

  if (flow.signalKey === "daily_media" && flow.stage === "record_update") {
    return "먼저 /api/daily-records/:recordId/videos requestId 로그를 열고 첨부 파일 소유권, 기록 상태, 파일 연결 오류를 확인하세요.";
  }

  if (flow.signalKey === "health_record" && flow.stage === "health_file_upload") {
    return "먼저 POST /api/files requestId 로그를 열고 health_record_source 용도, 파일 정책, 반려동물 소유권, 저장소 저장 오류를 확인하세요.";
  }

  if (flow.signalKey === "health_record" && flow.stage === "health_file_confirm") {
    return "먼저 POST /api/files/:fileId/confirm requestId 로그를 열고 임시 파일 상태, 건강 기록 원본 소유권, 연결된 기록 또는 파일 불일치를 확인하세요.";
  }

  if (flow.signalKey === "health_record" && flow.stage === "health_analysis_process") {
    return "먼저 POST /api/record-drafts/health-file-analysis requestId 로그와 linkedFileId 기준 health_file_analysis 작업 행을 함께 열고 적재, 제공자 시간 초과, 파서 대체 경로, 연결된 기록 또는 파일 불일치를 확인하세요.";
  }

  if (flow.signalKey === "health_record" && flow.stage === "health_record_save") {
    return "먼저 POST/PATCH /api/records requestId 로그를 열고 record.type=health 분기, 측정값 정규화, 연결 파일 확인 및 저장 순서 충돌을 확인하세요.";
  }

  if (flow.signalKey === "receipt_record" && flow.stage === "receipt_extract") {
    return "먼저 POST /api/receipt-records/ai-extract requestId 로그를 열고 multipart receiptImage 업로드, 반려동물 소유권, 제공자 시간 초과 또는 대체 경로, 자동 저장되지 않는 초안 경고를 확인하세요.";
  }

  if (flow.signalKey === "receipt_record" && flow.stage === "receipt_save") {
    return "먼저 POST/PATCH/DELETE /api/receipt-records requestId 로그를 열고 보호자 명시 저장 입력값, purchasedAt 또는 amount 또는 category 정규화, 영수증 소유권 또는 소프트 삭제 충돌을 확인하세요.";
  }

  if (flow.signalKey === "receipt_record" && flow.stage === "receipt_category_create") {
    return "먼저 POST /api/receipt-categories requestId 로그를 열고 사용자 정의 라벨 검증, 표준 카테고리 보호, 중복 라벨 충돌을 확인하세요.";
  }

  if (flow.signalKey === "receipt_record" && flow.stage === "receipt_category_update") {
    return "먼저 PATCH /api/receipt-categories/:categoryId requestId 로그를 열고 활성 카테고리 소유권, 중복 라벨 이름 변경, 소프트 삭제된 카테고리 수정 시도를 확인하세요.";
  }

  if (flow.signalKey === "receipt_record" && flow.stage === "receipt_category_delete") {
    return "먼저 DELETE /api/receipt-categories/:categoryId requestId 로그를 열고 활성 카테고리 소유권, 이미 삭제된 상태, 연결된 영수증 대체 정책을 확인하세요.";
  }

  if (flow.signalKey === "file_lifecycle" && flow.stage === "file_cleanup") {
    return "먼저 file_lifecycle requestId 로그를 열고 expired-temp 스케줄 또는 작업자인지 deleted-file 스케줄 또는 작업자인지 구분한 뒤 attemptedCount, cleanedFileIds, 저장소 제공자 삭제 오류와 storageDeletedAt 반영 여부를 확인하세요.";
  }

  if (flow.statusCode !== undefined && flow.statusCode >= 500) {
    return "먼저 requestId 기준 서버 오류 로그를 열고 스택 추적과 안정 오류 코드를 확인하세요.";
  }

  return "먼저 requestId 기준 API 완료 로그를 열고 errorCode와 응답 상태를 확인하세요.";
}

function buildSelectedScopeLabel(rows: AdminFailedFlow[], locale: AdminDashboardLocale): string {
  const first = rows[0];

  if (!first) {
    return text(locale, "latest failed flows", "최신 오류 흐름");
  }

  if (first.requestId) {
    return `${text(locale, "request", "요청")} ${first.requestId}`;
  }

  if (first.correlationId) {
    return `${text(locale, "correlation", "상관관계")} ${first.correlationId}`;
  }

  if (first.deviceInstallId) {
    return `${text(locale, "device", "기기")} ${toDeviceInstallIdLabel(first.deviceInstallId)}`;
  }

  if (first.release || first.appVersion) {
    return [first.release, first.appVersion].filter(Boolean).join(" / ");
  }

  return text(locale, "latest failed flows", "최신 오류 흐름");
}

function summarizeTopError(rows: AdminFailedFlow[]) {
  const counts = new Map<string, number>();

  for (const row of rows) {
    counts.set(row.errorCode, (counts.get(row.errorCode) ?? 0) + 1);
  }

  let topErrorCode = "-";
  let topErrorCount = 0;

  for (const [errorCode, count] of counts.entries()) {
    if (count > topErrorCount) {
      topErrorCode = errorCode;
      topErrorCount = count;
    }
  }

  return {
    topErrorCodeLabel: topErrorCode,
    topErrorCountLabel: formatAdminCount(topErrorCount),
  };
}

function countDistinctValues(values: Array<string | undefined>): string {
  return formatAdminCount(new Set(values.filter((value): value is string => Boolean(value))).size);
}

function createIncidentFocusedUserSummaryHref(
  href: string | undefined,
  matchedIncidentArtifact: AdminIncidentLogArtifactData | undefined,
): string | undefined {
  if (!href || !matchedIncidentArtifact) {
    return href;
  }

  const url = new URL(href, "https://petlog.local");
  const focusedParams = appendUserIncidentWorkspaceFocus(url.searchParams, {
    artifactId: matchedIncidentArtifact.artifactId,
  });
  return `${url.pathname}?${focusedParams.toString()}`;
}

function buildIncidentRouteActions(
  flow: AdminFailedFlow,
  drilldownTarget: AdminFailedFlowDrilldownTarget,
  matchedIncidentArtifact: AdminIncidentLogArtifactData | undefined,
  requestLogsUrl: string | undefined,
  locale: AdminDashboardLocale,
): FailedFlowIncidentRouteActionViewModel[] {
  const requestSummary = buildRequestEvidenceLogAvailabilitySummary(
    flow,
    drilldownTarget,
    requestLogsUrl,
    locale,
  );
  const requestLinkLabel =
    requestLogsUrl !== undefined
      ? text(locale, "Open Cloud Logging", "Cloud Logging 열기")
      : flow.requestId && drilldownTarget.request !== undefined
        ? text(locale, "Open request scope", "요청 범위 열기")
        : drilldownTarget.correlation !== undefined
          ? text(locale, "Open correlation scope", "상관관계 범위 열기")
          : undefined;
  const requestHref = requestLogsUrl ?? drilldownTarget.request ?? drilldownTarget.correlation;
  const userSummaryHref = createIncidentFocusedUserSummaryHref(
    drilldownTarget.userSummary,
    matchedIncidentArtifact,
  );

  const userSummary =
    userSummaryHref !== undefined
      ? text(locale, "Open the affected user summary next to compare latest failure, latest save, and current risk in the same operator flow.", "같은 운영 흐름에서 최신 실패, 최신 저장, 현재 위험도를 비교할 수 있도록 영향을 받은 사용자 요약을 여세요.")
      : text(locale, "This failed flow does not expose a user hash. Continue with release regression or request/job context instead of user lookup.", "이 오류 흐름에는 사용자 해시가 없습니다. 사용자 조회 대신 릴리스 회귀 또는 요청이나 작업 문맥으로 이어가세요.");

  const releaseSummary =
    drilldownTarget.releaseRegression !== undefined
      ? text(locale, "Compare the same release/app bucket to decide whether this is a single-user incident or a wider regression.", "같은 릴리스 또는 앱 버전 버킷을 비교해 단일 사용자 사고인지 더 넓은 회귀인지 판단하세요.")
      : text(locale, "No release or appVersion was captured on this flow, so release regression comparison is not available for this incident.", "이 흐름에는 `release`나 `appVersion`이 수집되지 않아 릴리스 회귀 비교를 할 수 없습니다.");

  let jobsSummary =
    text(locale, "No linked job context is available on this flow. Use request logs and release regression as the next investigation steps.", "이 흐름에는 연결된 작업 문맥이 없습니다. 다음 조사 단계로 요청 로그 확인과 릴리스 회귀 비교를 진행하세요.");

  if (flow.signalKey === "file_lifecycle" && flow.stage === "file_cleanup") {
    jobsSummary =
      text(locale, "File cleanup does not create a durable admin job row. Keep the investigation on request logs, release regression, and upstream user context.", "파일 정리는 영속 관리자 작업 행을 만들지 않습니다. 요청 로그, 릴리스 회귀, 상위 사용자 문맥을 중심으로 조사하세요.");
  } else if (drilldownTarget.jobs !== undefined) {
    jobsSummary =
      text(locale, "If the request log alone is not enough, open the linked job scope to confirm queue, worker, or provider-side follow-up state.", "요청 로그만으로 충분하지 않다면 연결된 작업 범위를 열어 대기열, 작업자, 제공자 측 후속 상태를 확인하세요.");
  }

  const mediaRelevantFlow = isFailedFlowMediaRelevant(flow);

  const mediaSummary = !mediaRelevantFlow
    ? text(
        locale,
        "This failed flow does not currently point to a linked uploaded-media investigation path. Continue with request, user, release, or job evidence instead.",
        "이 오류 흐름은 아직 연결된 업로드 미디어 조사 경로를 가리키지 않습니다. 요청, 사용자, 릴리스, 작업 증거를 중심으로 계속 조사하세요.",
      )
    : drilldownTarget.userMedia !== undefined
      ? text(
          locale,
          "Re-open the affected user lookup with the same request or user context, then continue into the uploaded-media workspace to compare representative photo, artifact table, and trace linkage.",
          "같은 요청 또는 사용자 문맥으로 영향을 받은 사용자 조회를 다시 열고, 업로드 미디어 작업면에서 대표 사진, artifact 표, 추적 연결을 이어서 비교하세요.",
        )
      : text(
          locale,
          "This media-related failed flow is missing request, correlation, and user lookup context, so the uploaded-media workspace cannot be opened from here yet.",
          "이 미디어 관련 오류 흐름에는 요청, 상관관계, 사용자 조회 문맥이 없어 여기서 업로드 미디어 작업면을 바로 열 수 없습니다.",
        );

  return [
    {
      id: "requestLogs",
      label: text(locale, "1. Request log", "1. 요청 로그"),
      summary: requestSummary,
      href: requestHref,
      linkLabel: requestLinkLabel,
    },
    {
      id: "userLookup",
      label: text(locale, "2. User lookup", "2. 사용자 조회"),
      summary: userSummary,
      href: userSummaryHref,
      linkLabel: userSummaryHref ? text(locale, "Open user summary", "사용자 요약 열기") : undefined,
    },
    {
      id: "releaseRegression",
      label: text(locale, "3. Release regression", "3. 릴리스 회귀"),
      summary: releaseSummary,
      href: drilldownTarget.releaseRegression,
      linkLabel: drilldownTarget.releaseRegression ? text(locale, "Open release regression", "릴리스 회귀 열기") : undefined,
    },
    {
      id: "jobsFallback",
      label: text(locale, "4. Jobs fallback", "4. 작업 보조 조사"),
      summary: jobsSummary,
      href: drilldownTarget.jobs,
      linkLabel: drilldownTarget.jobs ? text(locale, "Open jobs fallback", "작업 화면 열기") : undefined,
    },
    {
      id: "mediaLookup",
      label: text(locale, "5. Media workspace", "5. 미디어 작업면"),
      summary: mediaSummary,
      href: drilldownTarget.userMedia,
      linkLabel:
        mediaRelevantFlow && drilldownTarget.userMedia
          ? text(locale, "Open user media lookup", "사용자 미디어 조회 열기")
          : undefined,
    },
  ];
}

function buildMediaContext(
  flow: AdminFailedFlow,
  drilldownTarget: AdminFailedFlowDrilldownTarget,
  matchedArtifact: AdminMediaArtifactData | undefined,
  locale: AdminDashboardLocale,
): FailedFlowDetailViewModel["mediaContext"] {
  if (!isFailedFlowMediaRelevant(flow)) {
    return undefined;
  }

  const sourceScenarioLabel =
    matchedArtifact?.sourceScenario ?? inferFailedFlowMediaScenarioKey(flow) ?? "unknown";

  const inferredLinkedEntity = inferFailedFlowMediaLinkedEntity(flow);
  const linkedEntityLabel = matchedArtifact?.linkedEntityType && matchedArtifact.linkedEntityId
    ? `${matchedArtifact.linkedEntityType}:${matchedArtifact.linkedEntityId}`
    : inferredLinkedEntity
      ? `${inferredLinkedEntity.linkedEntityType}:${inferredLinkedEntity.linkedEntityId}`
      : "-";

  const mediaKindLabel =
    matchedArtifact
      ? matchedArtifact.mediaKind === "image"
        ? text(locale, "image", "image")
        : matchedArtifact.mediaKind === "video"
          ? text(locale, "video", "video")
          : matchedArtifact.mediaKind === "document"
            ? text(locale, "document", "document")
            : text(locale, "unknown", "unknown")
      : flow.signalKey === "daily_media"
        ? text(locale, "image_or_video", "image_or_video")
        : flow.signalKey === "community_post"
          ? text(locale, "image", "image")
        : flow.signalKey === "health_record"
          ? text(locale, "document", "document")
          : flow.signalKey === "file_lifecycle"
            ? text(locale, "unknown", "unknown")
            : text(locale, "image", "image");

  const headline =
    locale === "en"
      ? `Linked media context: ${sourceScenarioLabel}`
      : `연결된 미디어 문맥: ${sourceScenarioLabel}`;

  const summary = drilldownTarget.userMedia
    ? matchedArtifact
      ? text(
          locale,
          "This incident already matches a concrete media artifact inventory row. Re-open the user media workspace to compare artifact role, preview or download path, and the latest request trace without searching raw file rows first.",
          "이 incident는 이미 구체적인 미디어 artifact inventory 행과 연결됩니다. 원시 파일 행을 먼저 찾지 말고 사용자 미디어 작업면을 다시 열어 artifact 역할, 미리보기 또는 다운로드 경로, 최신 요청 추적을 함께 비교하세요.",
        )
      : text(
          locale,
          "Open the user media workspace from this incident to compare the representative photo, selected artifact workspace, and request trace without searching raw file rows first.",
          "이 incident에서 바로 사용자 미디어 작업면을 열어 대표 사진, 선택 artifact 작업면, 요청 추적을 파일 행 검색 없이 함께 비교하세요.",
        )
    : text(
        locale,
        "This incident is media-related, but it is still missing enough request or user scope to reopen the uploaded-media workspace directly.",
        "이 incident는 미디어 관련이지만 업로드 미디어 작업면을 직접 다시 열 만큼의 요청 또는 사용자 문맥이 아직 부족합니다.",
      );

  const inventoryStateLabel = matchedArtifact
    ? text(locale, "Matched artifact inventory row", "매칭된 artifact inventory 행")
    : drilldownTarget.userMedia
      ? text(locale, "Inventory row not matched yet", "inventory 행이 아직 매칭되지 않음")
      : text(locale, "Workspace context unavailable", "작업면 문맥 없음");

  const missingReasonLabel = matchedArtifact
    ? undefined
    : drilldownTarget.userMedia
      ? text(
          locale,
          "The failed-flow trace is media-related, but no concrete artifact row matched by file, request, correlation, or linked entity yet.",
          "이 오류 흐름은 미디어 관련이지만 파일, 요청, 상관관계, 연결 엔터티 기준으로 아직 구체적인 artifact 행이 매칭되지 않았습니다.",
        )
      : text(
          locale,
          "The failed-flow row does not carry enough request, correlation, or user scope to reopen the uploaded-media workspace directly.",
          "이 오류 흐름 행에는 업로드 미디어 작업면을 직접 다시 열 만큼의 요청, 상관관계, 사용자 문맥이 없습니다.",
        );

  const nextActionLabel = matchedArtifact
    ? undefined
    : drilldownTarget.userMedia
      ? text(
          locale,
          "Open the user media workspace next to inspect representative photo, scenario coverage, and linked file context inside the same incident.",
          "같은 incident 안에서 대표 사진, 시나리오 coverage, 연결 파일 문맥을 확인하려면 다음으로 사용자 미디어 작업면을 여세요.",
        )
      : text(
          locale,
          "Continue with request logs, release regression, and any recoverable user scope before retrying media re-entry.",
          "미디어 재진입을 다시 시도하기 전에 요청 로그, 릴리스 회귀, 복구 가능한 사용자 문맥을 먼저 확인하세요.",
        );

  return {
    headline,
    sourceScenarioLabel,
    artifactIdentityLabel: matchedArtifact?.artifactId ?? flow.fileId ?? "-",
    linkedEntityLabel,
    mediaKindLabel,
    summary,
    inventoryStateLabel,
    missingReasonLabel,
    nextActionLabel,
    relationTypeLabel: matchedArtifact
      ? formatMediaRelationTypeLabel(matchedArtifact.relationType, locale)
      : undefined,
    artifactRoleLabel: matchedArtifact
      ? formatMediaArtifactRoleLabel(matchedArtifact.artifactRole, locale)
      : undefined,
    previewAvailabilityLabel: matchedArtifact
      ? formatMediaAvailabilityLabel(matchedArtifact.previewAvailability, locale)
      : undefined,
    downloadAvailabilityLabel: matchedArtifact
      ? formatMediaAvailabilityLabel(matchedArtifact.downloadAvailability, locale)
      : undefined,
    fallbackReasonLabel: matchedArtifact
      ? formatMediaFallbackReasonLabel(matchedArtifact.fallbackReason, locale)
      : undefined,
    binaryReviewPathLabel: matchedArtifact
      ? pickArtifactBinaryReviewPathLabel(matchedArtifact, locale)
      : undefined,
    workspaceHref: drilldownTarget.userMedia,
    workspaceLinkLabel: drilldownTarget.userMedia
      ? text(locale, "Open user media lookup", "사용자 미디어 조회 열기")
      : undefined,
  };
}

function buildSameDeviceLogContext(
  flow: AdminFailedFlow,
  matchedIncidentArtifact: AdminIncidentLogArtifactData | undefined,
  drilldownTarget: AdminFailedFlowDrilldownTarget,
  locale: AdminDashboardLocale,
): FailedFlowDetailViewModel["sameDeviceLogContext"] {
  if (!matchedIncidentArtifact) {
    return undefined;
  }

  const baseWorkspaceHref = drilldownTarget.userMedia ?? drilldownTarget.userSummary;
  const workspaceHref = baseWorkspaceHref
    ? (() => {
        const url = new URL(baseWorkspaceHref, "https://petlog.local");
        const focusedParams = appendUserIncidentWorkspaceFocus(url.searchParams, {
          artifactId: matchedIncidentArtifact.artifactId,
        });
        return `${url.pathname}?${focusedParams.toString()}`;
      })()
    : undefined;
  const requestTraceHref = matchedIncidentArtifact.latestTraceKey?.requestId
    ? `/admin/failed-flows?${new URLSearchParams({ requestId: matchedIncidentArtifact.latestTraceKey.requestId }).toString()}`
    : matchedIncidentArtifact.latestTraceKey?.correlationId
      ? `/admin/failed-flows?${new URLSearchParams({ correlationId: matchedIncidentArtifact.latestTraceKey.correlationId }).toString()}`
      : undefined;

  return {
    headline: locale === "en" ? "Same-device incident log context" : "동일 기기 incident 로그 문맥",
    artifactIdentityLabel: matchedIncidentArtifact.artifactId,
    captureTriggerLabel: formatIncidentLogCaptureTriggerLabel(
      matchedIncidentArtifact.captureTrigger,
      locale,
    ),
    previewReadinessLabel: formatIncidentLogPreviewReadinessLabel(
      matchedIncidentArtifact.previewReadiness,
      locale,
    ),
    downloadReadinessLabel: formatIncidentLogDownloadReadinessLabel(
      matchedIncidentArtifact.downloadReadiness,
      locale,
    ),
    bundleHashPresenceLabel: formatIncidentLogBundleHashPresenceLabel(
      matchedIncidentArtifact.bundleHashPresence,
      locale,
    ),
    manifestVersionLabel: matchedIncidentArtifact.manifestVersion ?? "-",
    deliveryPathStateLabel: pickIncidentLogDeliveryPathStateLabel(
      matchedIncidentArtifact,
      locale,
    ),
    previewHeadlineLabel: matchedIncidentArtifact.previewExcerpt?.headline ?? "-",
    latestRequestIdLabel: matchedIncidentArtifact.latestTraceKey?.requestId ?? "-",
    latestCorrelationIdLabel: matchedIncidentArtifact.latestTraceKey?.correlationId ?? "-",
    summary: workspaceHref
      ? text(
          locale,
          "This failed flow already matches a same-device log artifact. Re-open the user incident workspace to read the inline excerpt and issue the same bundle download from the matched artifact identity.",
          "이 오류 흐름은 이미 same-device log artifact와 연결됩니다. 사용자 incident 작업면을 다시 열어 inline excerpt를 읽고 같은 artifact identity에서 bundle 다운로드를 발급하세요.",
        )
      : text(
          locale,
          "A same-device log artifact is matched, but this failed flow still lacks enough user workspace context to reopen the operator incident workspace directly.",
          "same-device log artifact는 매칭됐지만, 이 오류 흐름에는 운영자 incident 작업면을 직접 다시 열 만큼의 사용자 작업면 문맥이 아직 부족합니다.",
        ),
    workspaceHref,
    workspaceLinkLabel: workspaceHref
      ? text(locale, "Open user incident workspace", "사용자 incident 작업면 열기")
      : undefined,
    requestTraceHref,
    requestTraceLinkLabel: requestTraceHref
      ? text(locale, "Open matched failed-flow trace", "매칭된 오류 흐름 추적 열기")
      : undefined,
  };
}

export function toFailedFlowDetailViewModel(
  flow: AdminFailedFlow,
  matchedArtifact: AdminMediaArtifactData | undefined,
  matchedIncidentArtifact: AdminIncidentLogArtifactData | undefined,
  runtimeConfig?: Pick<AdminDashboardRuntimeConfig, "observability">,
  locale: AdminDashboardLocale = "ko",
): FailedFlowDetailViewModel {
  const status = toRowStatus(flow.status, locale);
  const drilldownTarget = buildFailedFlowDrilldownTarget(flow);
  const incidentSummary = buildIncidentSummary(flow, locale);
  const firstCheckLabel = buildFirstCheckLabel(flow, locale);
  const requestLogsUrl = buildCloudLoggingRequestLogsUrl(
    {
      projectId: runtimeConfig?.observability?.cloudProjectId,
      cloudRunServiceName: runtimeConfig?.observability?.cloudRunServiceName,
    },
    flow.requestId,
  );

  return {
    id: flow.id,
    createdAtLabel: formatAdminDate(flow.createdAt),
    signalKey: flow.signalKey,
    stage: flow.stage,
    status: status.status,
    statusLabel: status.statusLabel,
    errorCode: flow.errorCode,
    statusCodeLabel: toStatusCodeLabel(flow.statusCode),
    requestId: flow.requestId,
    correlationId: flow.correlationId,
    jobId: flow.jobId,
    userIdHash: flow.userIdHash,
    accountIdentityLabel: formatAdminAccountIdentityLabel(flow.accountIdentity, locale),
    accountProviderIdentityLabel: formatAdminProviderIdentityLabel(flow.accountIdentity, locale),
    accountLinkedProviderCountLabel: formatAdminLinkedProviderCountLabel(flow.accountIdentity, locale),
    petIdHash: flow.petIdHash,
    recordId: flow.recordId,
    receiptId: flow.receiptId,
    fileId: flow.fileId,
    deletionJobId: flow.deletionJobId,
    release: flow.release,
    appVersion: flow.appVersion,
    runtimeAppBuildNumberLabel: formatAdminTelemetryAppBuildNumberLabel(flow.runtimeAppBuildNumber, locale),
    platformLabel: toPlatformLabel(flow.platform),
    runtimeLocaleLabel: formatAdminTelemetryLocaleLabel(flow.runtimeLocale, locale),
    runtimeTimezoneOffsetBucketLabel: formatAdminTelemetryTimezoneOffsetBucketLabel(
      flow.runtimeTimezoneOffsetBucket,
      locale,
    ),
    runtimeTimezoneNameBucketLabel: formatAdminTelemetryTimezoneNameBucketLabel(flow.runtimeTimezoneNameBucket, locale),
    runtimeDeviceModelBucketLabel: formatAdminTelemetryDeviceModelBucketLabel(flow.runtimeDeviceModelBucket, locale),
    runtimeDeviceModelNameBucketLabel: formatAdminTelemetryDeviceModelNameBucketLabel(
      flow.runtimeDeviceModelNameBucket,
      locale,
    ),
    runtimeOsMajorBucketLabel: formatAdminTelemetryOsMajorBucketLabel(flow.runtimeOsMajorBucket, locale),
    runtimeOsVersionMinorBucketLabel: formatAdminTelemetryOsVersionMinorBucketLabel(
      flow.runtimeOsVersionMinorBucket,
      locale,
    ),
    runtimeNetworkTypeBucketLabel: formatAdminTelemetryNetworkTypeBucketLabel(flow.runtimeNetworkTypeBucket, locale),
    runtimePhysicalDeviceBucketLabel: formatAdminTelemetryPhysicalDeviceBucketLabel(
      flow.runtimePhysicalDeviceBucket,
      locale,
    ),
    runtimeSummaryLabel: formatAdminTelemetryRuntimeSummaryLabel(
      {
        appBuildNumber: flow.runtimeAppBuildNumber,
        deviceModelNameBucket: flow.runtimeDeviceModelNameBucket,
        osVersionMinorBucket: flow.runtimeOsVersionMinorBucket,
        networkTypeBucket: flow.runtimeNetworkTypeBucket,
        physicalDeviceBucket: flow.runtimePhysicalDeviceBucket,
      },
      locale,
    ),
    providerKindLabel: formatAdminAuthProviderKindLabel(flow.providerKind, locale),
    reauthMethodBucketLabel: formatAdminTelemetryReauthMethodBucketLabel(flow.reauthMethodBucket, locale),
    recentAuthAgeBucketLabel: formatAdminTelemetryRecentAuthAgeBucketLabel(flow.recentAuthAgeBucket, locale),
    requestSaveBeforeExitLabel: formatAdminBooleanLabel(flow.requestSaveBeforeExit, locale),
    requestSuggestionUsedLabel: formatAdminBooleanLabel(flow.requestSuggestionUsed, locale),
    requestProfileCompletionStepLabel: formatAdminPetProfileCompletionStepLabel(
      flow.requestProfileCompletionStep,
      locale,
    ),
    requestAttemptCountBucketLabel: formatAdminPetAttemptCountBucketLabel(flow.requestAttemptCountBucket, locale),
    requestPhotoConfirmLatencyBucketLabel: formatAdminPetPhotoConfirmLatencyBucketLabel(
      flow.requestPhotoConfirmLatencyBucket,
      locale,
    ),
    requestDraftSourceBucketLabel: formatAdminTelemetryDraftSourceBucketLabel(flow.requestDraftSourceBucket, locale),
    requestMediaAttachCountBucketLabel: formatAdminTelemetryMediaAttachCountBucketLabel(
      flow.requestMediaAttachCountBucket,
      locale,
    ),
    requestSpeciesBucketLabel: formatAdminPetSpeciesBucketLabel(flow.requestSpeciesBucket, locale),
    requestBreedSelectionBucketLabel: formatAdminPetBreedSelectionBucketLabel(
      flow.requestBreedSelectionBucket,
      locale,
    ),
    requestTargetUserLookupKindLabel: formatAdminSocialTargetUserLookupKindLabel(
      flow.requestTargetUserLookupKind,
      locale,
    ),
    requestPublicPetCountBucketLabel: formatAdminSocialPublicPetCountBucketLabel(
      flow.requestPublicPetCountBucket,
      locale,
    ),
    requestProfileMutationFieldSetBucketLabel: formatAdminSocialProfileMutationFieldSetBucketLabel(
      flow.requestProfileMutationFieldSetBucket,
      locale,
    ),
    requestRoomKindLabel: formatAdminChatRoomKindLabel(flow.requestRoomKind, locale),
    requestMessageLengthBucketLabel: formatAdminChatMessageLengthBucketLabel(
      flow.requestMessageLengthBucket,
      locale,
    ),
    requestMembershipCountBucketLabel: formatAdminChatMembershipCountBucketLabel(
      flow.requestMembershipCountBucket,
      locale,
    ),
    requestScheduleRecurrenceBucketLabel: formatAdminMedicationScheduleRecurrenceBucketLabel(
      flow.requestScheduleRecurrenceBucket,
      locale,
    ),
    requestDosePresenceLabel: formatAdminMedicationDosePresenceLabel(flow.requestDosePresence, locale),
    requestOccurrenceActionKindLabel: formatAdminMedicationOccurrenceActionKindLabel(
      flow.requestOccurrenceActionKind,
      locale,
    ),
    requestSnoozeWindowBucketLabel: formatAdminMedicationSnoozeWindowBucketLabel(
      flow.requestSnoozeWindowBucket,
      locale,
    ),
    requestRideVisitLeadTimeBucketLabel: formatAdminRideVisitLeadTimeBucketLabel(
      flow.requestRideVisitLeadTimeBucket,
      locale,
    ),
    requestRideLongestAddressLengthBucketLabel: formatAdminRideLongestAddressLengthBucketLabel(
      flow.requestRideLongestAddressLengthBucket,
      locale,
    ),
    requestRideMemoProvidedLabel: formatAdminBooleanLabel(flow.requestRideMemoProvided, locale),
    requestCommunityUploadSourceBucketLabel: formatAdminCommunityUploadSourceBucketLabel(
      flow.requestCommunityUploadSourceBucket,
      locale,
    ),
    requestCommunityContentLengthBucketLabel: formatAdminCommunityContentLengthBucketLabel(
      flow.requestCommunityContentLengthBucket,
      locale,
    ),
    requestCommunityCommentLengthBucketLabel: formatAdminCommunityContentLengthBucketLabel(
      flow.requestCommunityCommentLengthBucket,
      locale,
    ),
    requestCommunityImageCountBucketLabel: formatAdminCommunityImageCountBucketLabel(
      flow.requestCommunityImageCountBucket,
      locale,
    ),
    requestCommunityPetContextAttachedLabel: formatAdminBooleanLabel(
      flow.requestCommunityPetContextAttached,
      locale,
    ),
    requestCommunityTopicTagLabel: formatAdminCommunityTopicTagLabel(flow.requestCommunityTopicTag, locale),
    requestCommunityRetryCountBucketLabel: formatAdminPetAttemptCountBucketLabel(
      flow.requestCommunityRetryCountBucket,
      locale,
    ),
    requestReceiptExtractSourceBucketLabel: formatAdminReceiptExtractSourceBucketLabel(
      flow.requestReceiptExtractSourceBucket,
      locale,
    ),
    requestReceiptImageByteSizeBucketLabel: formatAdminReceiptImageByteSizeBucketLabel(
      flow.requestReceiptImageByteSizeBucket,
      locale,
    ),
    requestReceiptOcrConfidenceBucketLabel: formatAdminReceiptOcrConfidenceBucketLabel(
      flow.requestReceiptOcrConfidenceBucket,
      locale,
    ),
    requestReceiptManualOverrideLabel: formatAdminBooleanLabel(flow.requestReceiptManualOverride, locale),
    requestReceiptCategoryMutationTypeLabel: formatAdminReceiptCategoryMutationTypeLabel(
      flow.requestReceiptCategoryMutationType,
      locale,
    ),
    requestHealthFileSizeBucketLabel: formatAdminHealthFileSizeBucketLabel(
      flow.requestHealthFileSizeBucket,
      locale,
    ),
    requestHealthFileTypeBucketLabel: formatAdminHealthFileTypeBucketLabel(
      flow.requestHealthFileTypeBucket,
      locale,
    ),
    requestHealthAnalysisFallbackPathLabel: formatAdminHealthAnalysisFallbackPathLabel(
      flow.requestHealthAnalysisFallbackPath,
      locale,
    ),
    requestHealthConfirmToSaveLatencyBucketLabel: formatAdminHealthConfirmToSaveLatencyBucketLabel(
      flow.requestHealthConfirmToSaveLatencyBucket,
      locale,
    ),
    deviceInstallId: flow.deviceInstallId,
    deviceInstallIdLabel: toDeviceInstallIdLabel(flow.deviceInstallId),
    lastEventNameLabel: toEventLabel(flow.lastEventName),
    lastEventAtLabel: formatAdminDate(flow.lastEventAt),
    drilldownTarget,
    jobLabel: flow.jobId ?? flow.deletionJobId,
    observability: {
      incidentSummary,
      firstCheckLabel,
      requestLogsUrl,
      similarFailuresUrl: buildCloudLoggingSimilarFailuresUrl({
        projectId: runtimeConfig?.observability?.cloudProjectId,
        cloudRunServiceName: runtimeConfig?.observability?.cloudRunServiceName,
        feature: flow.feature,
        useCase: flow.useCase,
        errorCode: flow.errorCode,
      }),
    },
    requestEvidence: buildRequestEvidence(flow, drilldownTarget, requestLogsUrl, locale),
    mediaContext: buildMediaContext(flow, drilldownTarget, matchedArtifact, locale),
    sameDeviceLogContext: buildSameDeviceLogContext(
      flow,
      matchedIncidentArtifact,
      drilldownTarget,
      locale,
    ),
    incidentRoute: {
      headline: incidentSummary,
      firstCheckLabel,
      actions: buildIncidentRouteActions(
        flow,
        drilldownTarget,
        matchedIncidentArtifact,
        requestLogsUrl,
        locale,
      ),
    },
  };
}

function toFailedFlowRowViewModel(
  flow: AdminFailedFlow,
  matchedArtifact: AdminMediaArtifactData | undefined,
  matchedIncidentArtifact: AdminIncidentLogArtifactData | undefined,
  locale: AdminDashboardLocale,
  runtimeConfig?: Pick<AdminDashboardRuntimeConfig, "observability">,
): FailedFlowRowViewModel {
  const detail = toFailedFlowDetailViewModel(
    flow,
    matchedArtifact,
    matchedIncidentArtifact,
    runtimeConfig,
    locale,
  );

  return {
    id: flow.id,
    createdAt: flow.createdAt,
    createdAtLabel: detail.createdAtLabel,
    feature: flow.feature,
    useCase: flow.useCase,
    signalKey: flow.signalKey,
    stage: flow.stage,
    status: detail.status,
    statusLabel: detail.statusLabel,
    errorCode: detail.errorCode,
    statusCodeLabel: detail.statusCodeLabel,
    requestId: detail.requestId,
    correlationId: detail.correlationId,
    jobId: detail.jobId,
    release: detail.release,
    appVersion: detail.appVersion,
    runtimeAppBuildNumberLabel: detail.runtimeAppBuildNumberLabel,
    platformLabel: detail.platformLabel,
    runtimeLocaleLabel: detail.runtimeLocaleLabel,
    runtimeTimezoneOffsetBucketLabel: detail.runtimeTimezoneOffsetBucketLabel,
    runtimeTimezoneNameBucketLabel: detail.runtimeTimezoneNameBucketLabel,
    runtimeDeviceModelBucketLabel: detail.runtimeDeviceModelBucketLabel,
    runtimeDeviceModelNameBucketLabel: detail.runtimeDeviceModelNameBucketLabel,
    runtimeOsMajorBucketLabel: detail.runtimeOsMajorBucketLabel,
    runtimeOsVersionMinorBucketLabel: detail.runtimeOsVersionMinorBucketLabel,
    runtimeNetworkTypeBucketLabel: detail.runtimeNetworkTypeBucketLabel,
    runtimePhysicalDeviceBucketLabel: detail.runtimePhysicalDeviceBucketLabel,
    providerKindLabel: detail.providerKindLabel,
    reauthMethodBucketLabel: detail.reauthMethodBucketLabel,
    recentAuthAgeBucketLabel: detail.recentAuthAgeBucketLabel,
    requestSaveBeforeExitLabel: detail.requestSaveBeforeExitLabel,
    requestSuggestionUsedLabel: detail.requestSuggestionUsedLabel,
    requestProfileCompletionStepLabel: detail.requestProfileCompletionStepLabel,
    requestAttemptCountBucketLabel: detail.requestAttemptCountBucketLabel,
    requestPhotoConfirmLatencyBucketLabel: detail.requestPhotoConfirmLatencyBucketLabel,
    requestDraftSourceBucketLabel: detail.requestDraftSourceBucketLabel,
    requestMediaAttachCountBucketLabel: detail.requestMediaAttachCountBucketLabel,
    requestSpeciesBucketLabel: detail.requestSpeciesBucketLabel,
    requestBreedSelectionBucketLabel: detail.requestBreedSelectionBucketLabel,
    requestTargetUserLookupKindLabel: detail.requestTargetUserLookupKindLabel,
    requestPublicPetCountBucketLabel: detail.requestPublicPetCountBucketLabel,
    requestProfileMutationFieldSetBucketLabel: detail.requestProfileMutationFieldSetBucketLabel,
    requestRoomKindLabel: detail.requestRoomKindLabel,
    requestMessageLengthBucketLabel: detail.requestMessageLengthBucketLabel,
    requestMembershipCountBucketLabel: detail.requestMembershipCountBucketLabel,
    requestScheduleRecurrenceBucketLabel: detail.requestScheduleRecurrenceBucketLabel,
    requestDosePresenceLabel: detail.requestDosePresenceLabel,
    requestOccurrenceActionKindLabel: detail.requestOccurrenceActionKindLabel,
    requestSnoozeWindowBucketLabel: detail.requestSnoozeWindowBucketLabel,
    requestRideVisitLeadTimeBucketLabel: detail.requestRideVisitLeadTimeBucketLabel,
    requestRideLongestAddressLengthBucketLabel: detail.requestRideLongestAddressLengthBucketLabel,
    requestRideMemoProvidedLabel: detail.requestRideMemoProvidedLabel,
    requestCommunityUploadSourceBucketLabel: detail.requestCommunityUploadSourceBucketLabel,
    requestCommunityContentLengthBucketLabel: detail.requestCommunityContentLengthBucketLabel,
    requestCommunityCommentLengthBucketLabel: detail.requestCommunityCommentLengthBucketLabel,
    requestCommunityImageCountBucketLabel: detail.requestCommunityImageCountBucketLabel,
    requestCommunityPetContextAttachedLabel: detail.requestCommunityPetContextAttachedLabel,
    requestCommunityTopicTagLabel: detail.requestCommunityTopicTagLabel,
    requestCommunityRetryCountBucketLabel: detail.requestCommunityRetryCountBucketLabel,
    requestReceiptExtractSourceBucketLabel: detail.requestReceiptExtractSourceBucketLabel,
    requestReceiptImageByteSizeBucketLabel: detail.requestReceiptImageByteSizeBucketLabel,
    requestReceiptOcrConfidenceBucketLabel: detail.requestReceiptOcrConfidenceBucketLabel,
    requestReceiptManualOverrideLabel: detail.requestReceiptManualOverrideLabel,
    requestReceiptCategoryMutationTypeLabel: detail.requestReceiptCategoryMutationTypeLabel,
    requestHealthFileSizeBucketLabel: detail.requestHealthFileSizeBucketLabel,
    requestHealthFileTypeBucketLabel: detail.requestHealthFileTypeBucketLabel,
    requestHealthAnalysisFallbackPathLabel: detail.requestHealthAnalysisFallbackPathLabel,
    requestHealthConfirmToSaveLatencyBucketLabel:
      detail.requestHealthConfirmToSaveLatencyBucketLabel,
    deviceInstallId: flow.deviceInstallId,
    deviceInstallIdLabel: detail.deviceInstallIdLabel,
    runtimeSummaryLabel: detail.runtimeSummaryLabel,
    lastEventNameLabel: detail.lastEventNameLabel,
    lastEventAtLabel: detail.lastEventAtLabel,
    userIdHash: flow.userIdHash,
    accountIdentityLabel: detail.accountIdentityLabel,
    accountProviderIdentityLabel: detail.accountProviderIdentityLabel,
    accountLinkedProviderCountLabel: detail.accountLinkedProviderCountLabel,
    petIdHash: flow.petIdHash,
    recordId: flow.recordId,
    receiptId: flow.receiptId,
    fileId: flow.fileId,
    deletionJobId: flow.deletionJobId,
    drilldownTarget: detail.drilldownTarget,
    detail,
  };
}

export function toFailedFlowsViewModel(
  page: AdminFailedFlowPage,
  meta: AdminQueryMeta,
  matchedArtifactsByFlowId: ReadonlyMap<string, AdminMediaArtifactData>,
  matchedIncidentLogArtifactsByFlowId: ReadonlyMap<string, AdminIncidentLogArtifactData>,
  runtimeConfig?: Pick<AdminDashboardRuntimeConfig, "observability">,
  locale: AdminDashboardLocale = "ko",
): FailedFlowsViewModel {
  const distinctDeviceCountLabel = countDistinctValues(page.items.map((item) => item.deviceInstallId));
  const distinctUserCountLabel = countDistinctValues(page.items.map((item) => item.userIdHash));
  const topErrorSummary = summarizeTopError(page.items);

  return {
    totalCountLabel: formatAdminCount(page.totalCount ?? page.items.length),
    generatedAtLabel: formatAdminDate(meta.generatedAt),
    requestId: meta.requestId,
    nextCursor: meta.nextCursor,
    selectedScopeLabel: buildSelectedScopeLabel(page.items, locale),
    distinctDeviceCountLabel,
    distinctUserCountLabel,
    topErrorCodeLabel: topErrorSummary.topErrorCodeLabel,
    topErrorCountLabel: topErrorSummary.topErrorCountLabel,
    observabilityAggregate: buildAdminObservabilityAggregateViewModel(page.observabilityAggregate, locale, [
      "failureReasonBucketSummary",
    ]),
    rowHotspotSnapshot: buildAdminFailedFlowRowHotspotViewModel(page.items, locale),
    rows: page.items.map((item) =>
      toFailedFlowRowViewModel(
        item,
        matchedArtifactsByFlowId.get(item.id),
        matchedIncidentLogArtifactsByFlowId.get(item.id),
        locale,
        runtimeConfig,
      ),
    ),
    sourceFreshness: Object.entries(meta.sourceFreshness)
      .sort(([leftSource], [rightSource]) => leftSource.localeCompare(rightSource))
      .map(([source, freshness]) => ({
        source,
        freshness,
      })),
  };
}
